import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { pointerDragStart, pointerDragEnd, zoomDragStyle } from "../lib/pointerDrop";
import { Link } from "react-router-dom";
import { Settings2, ChevronLeft, ChevronRight, Check, Save, Trash2, Eye, EyeOff, Undo2, Redo2, Flag, ClipboardList, Keyboard, X } from "lucide-react";
import { Slot, buildSz, G_LABELS, STRENGTH, DEFAULT_BADGES, BADGE_PLACEMENTS } from "./LineupCard";
import { api, assetUrl } from "../lib/api";
import { NavPageTitle } from "./NavPageTitle";
import { RosterGamePicker } from "./RosterGamePicker";
import { useGameNav } from "../context/GameNavContext";

// While dragging, @hello-pangea/dnd positions the clone with `position: fixed`.
// Any ancestor with `transform` / `filter` / `backdrop-filter` (this app's glass
// UI) becomes the containing block for that fixed element, which makes the clone
// (a) drift away from the cursor and (b) collapse to the wrong size. Rendering the
// dragging clone through a portal to <body> escapes those ancestors so it tracks
// the mouse exactly and keeps its size.
const DRAG_PORTAL = typeof document !== "undefined" ? document.body : null;
const withPortal = (node, isDragging) => (isDragging && DRAG_PORTAL ? createPortal(node, DRAG_PORTAL) : node);

const filled = (arr) => (arr || []).filter((s) => s && (s.player_id || s.player_name));
const a2 = (pct) => Math.round(Math.max(0, Math.min(100, pct)) / 100 * 255).toString(16).padStart(2, "0");
const withA = (hex, pct) => `${hex}${a2(pct)}`;

const SPEC_TYPES = [["pp", "Power Play"], ["pk", "Penalty Kill"], ["ev", "Even Strength"]];
const SPEC_VIEWS = { pp: ["5v4", "5v3", "4v3"], pk: ["4v5", "3v5", "3v4"], ev: ["4v4", "3v3"] };
const LINE_LABELS = ["1ST LINE", "2ND LINE", "3RD LINE", "4TH LINE", "EXTRA"];
const PAIR_LABELS = ["1ST PAIR", "2ND PAIR", "3RD PAIR", "EXTRA"];
const SHOOT_PER_PAGE = 5;
const STAGE_LABELS = { preseason: "Preseason", regular: "Regular Season", playoffs: "Playoffs", playoff: "Playoffs" };
const STAGE_ORDER = { preseason: 0, regular: 1, playoff: 2, playoffs: 2 };

const TEXT_SIZES = {
    auto: buildSz(42, 11), small: buildSz(34, 9), medium: buildSz(46, 12), large: buildSz(56, 14), xl: buildSz(70, 16),
};
const COLOR_OPTS = [["primary", "Hex"], ["secondary", "2nd"], ["third", "3rd"], ["fourth", "4th"], ["custom", "Custom"]];
const resolveColor = (src, team, custom) => {
    if (src === "secondary") return team?.secondary_color || team?.primary_color || "#9D4CDD";
    if (src === "third") return team?.color_3 || team?.tertiary_color || team?.primary_color || "#9D4CDD";
    if (src === "fourth") return team?.color_4 || team?.color_3 || team?.primary_color || "#9D4CDD";
    if (src === "custom") return custom || "#9D4CDD";
    return team?.primary_color || "#9D4CDD";
};

// Opacity is a true 0–100% alpha: 100% = FULL solid colour, lower = more
// transparent. Border tracks the fill (slightly stronger) for definition.
const pillStyle = (color, op = 100) => ({ background: withA(color, op), border: `1px solid ${withA(color, Math.min(100, op + 12))}`, borderRadius: 14 });

// Default widget widths (flex-grow units). Admin-adjustable & global.
const DEFAULT_SPAN = { lines: 3, pairs: 2, goalies: 1, special: 3, shootout: 1, reserves: 1 };
const spanOf = (w) => Math.max(1, Math.min(4, w.span || DEFAULT_SPAN[w.key] || 1));

// Per-widget "sections" that each get their own colour + rename in admin.
const WIDGET_SECTIONS = {
    lines: [["fw0", "1ST LINE"], ["fw1", "2ND LINE"], ["fw2", "3RD LINE"], ["fw3", "4TH LINE"], ["fw4", "EXTRA"]],
    pairs: [["dp0", "1ST PAIR"], ["dp1", "2ND PAIR"], ["dp2", "3RD PAIR"], ["dp3", "EXTRA"]],
    goalies: [["goalies", "GOALIES"]],
    special: [["u1", "UNIT 1"], ["u2", "UNIT 2"]],
    shootout: [["so", "SHOOTOUT"]],
    reserves: [["reserves", "Healthy Scratch"], ["ir", "IR"], ["ltir", "LTIR"]],
};
const secColorOf = (w, secKey, team) => resolveColor((w.section_colors || {})[secKey] || w.color, team, w.custom);
const secTitleOf = (w, secKey, fallback) => { const t = (w.section_titles || {})[secKey]; return (t === undefined || t === "") ? fallback : t; };

// ---------- edit-mode helpers ----------
const EMPTY = { player_id: "", player_name: "" };
const clone = (o) => JSON.parse(JSON.stringify(o));
const posTag = (p) => { const s = (p?.position || "").toUpperCase(); if (s.startsWith("G")) return "G"; if (s === "D" || /D$/.test(s)) return "D"; return "F"; };
const getEV = (d, key) => { const [t, a, b] = key.split("-"); if (t === "fw") return d.forward_lines[+a][+b]; if (t === "dp") return d.d_pairs[+a][+b]; return d.goalies[+a]; };
const writeEV = (d, key, val) => { const [t, a, b] = key.split("-"); if (t === "fw") d.forward_lines[+a][+b] = val; else if (t === "dp") d.d_pairs[+a][+b] = val; else d.goalies[+a] = val; };
const parseId = (id) => {
    if (id.startsWith("pool:")) return { kind: "pool", pid: id.slice(5) };
    if (id.startsWith("reserves:")) return { kind: "res", idx: +id.slice(9) };
    if (id.startsWith("res:")) return { kind: "res", idx: +id.slice(4) };
    if (id.startsWith("so:")) return { kind: "so", idx: +id.slice(3) };
    if (id.startsWith("ir:")) return { kind: "ir", idx: +id.slice(3) };
    if (id.startsWith("ltir:")) return { kind: "ltir", idx: +id.slice(5) };
    if (id.startsWith("g3:")) return { kind: "g3", idx: +id.slice(3) };
    if (id.startsWith("slot:")) return { kind: "ev", key: id.slice(5) };
    if (id.startsWith("su:")) { const [, u, i] = id.split(":"); return { kind: "su", unit: u, idx: +i }; }
    return {};
};

// A player leaves the AVAILABLE pool once slotted in ANY exclusive area: lines,
// pairs, goalies, healthy scratch, IR, LTIR or 3rd Goalie. (PP/PK/shootout are
// duplicate-allowed, so they never remove a player from the pool.)
const placedSet = (d) => {
    const s = new Set();
    const add = (x) => x && x.player_id && s.add(x.player_id);
    (d.forward_lines || []).forEach((l) => (l || []).forEach(add));
    (d.d_pairs || []).forEach((l) => (l || []).forEach(add));
    (d.goalies || []).forEach(add);
    (d.reserves || []).forEach(add);
    ["ir", "ltir", "g3"].forEach((k) => ((d.special_units || {})[k] || []).forEach(add));
    return s;
};
const removeExclusive = (d, pid) => {
    d.forward_lines.forEach((l) => l.forEach((s, j) => { if (s.player_id === pid) l[j] = { ...EMPTY }; }));
    d.d_pairs.forEach((l) => l.forEach((s, j) => { if (s.player_id === pid) l[j] = { ...EMPTY }; }));
    d.goalies.forEach((s, j) => { if (s.player_id === pid) d.goalies[j] = { ...EMPTY }; });
    d.reserves = (d.reserves || []).filter((r) => r.player_id !== pid);
    ["ir", "ltir", "g3"].forEach((k) => { if (d.special_units[k]) d.special_units[k] = d.special_units[k].filter((r) => r.player_id !== pid); });
};
const clearSource = (d, s) => {
    if (s.kind === "ev") writeEV(d, s.key, { ...EMPTY });
    else if (s.kind === "res") d.reserves = (d.reserves || []).filter((_, i) => i !== s.idx);
    else if (s.kind === "so") d.shootout_order = (d.shootout_order || []).filter((_, i) => i !== s.idx);
    else if (["ir", "ltir", "g3"].includes(s.kind)) d.special_units[s.kind] = ((d.special_units || {})[s.kind] || []).filter((_, i) => i !== s.idx);
    else if (s.kind === "su" && d.special_units[s.unit]) d.special_units[s.unit][s.idx] = { ...EMPTY };
};

const hydrate = (src) => {
    src = src || {};
    const slot = (s) => (s && typeof s === "object" && (s.player_id || s.player_name)) ? { player_id: s.player_id || "", player_name: s.player_name || "", pos: s.pos, captain: s.captain || "", jersey: s.jersey } : { ...EMPTY };
    const line = (l, n) => { const out = (l || []).slice(0, n).map(slot); while (out.length < n) out.push({ ...EMPTY }); return out; };
    const fl = (src.forward_lines || []).slice(0, 5).map((l) => line(l, 3)); while (fl.length < 5) fl.push(line([], 3));
    const dp = (src.d_pairs || []).slice(0, 4).map((l) => line(l, 2)); while (dp.length < 4) dp.push(line([], 2));
    const su = {};
    if (src.special_units && typeof src.special_units === "object") {
        for (const [k, v] of Object.entries(src.special_units)) if (Array.isArray(v)) su[k] = v.map(slot).filter((x) => x.player_id || x.player_name || k.includes("_"));
    }
    ["ir", "ltir", "g3", "ea"].forEach((k) => { su[k] = (su[k] || []).filter((x) => x.player_id || x.player_name); });
    // 3rd Goalie retired as a slot → migrate anyone parked there to Healthy Scratch.
    const g3move = su.g3 || [];
    su.g3 = [];
    return {
        coach_id: src.coach_id || "",
        forward_lines: fl, d_pairs: dp,
        goalies: line(src.goalies, Math.max(2, (src.goalies || []).length || 2)),
        reserves: [...(src.reserves || []).filter((s) => s && (s.player_id || s.player_name)), ...g3move],
        special_units: su,
        shootout_order: (src.shootout_order || []).filter((s) => s && (s.player_id || s.player_name)),
    };
};

// Shared per-board badge config (jersey #, C/A, position placement) + team
// accent for the jersey chip. Provided once around the board so every Pill /
// EditSlot renders identical badges without threading props everywhere.
const BadgeCtx = React.createContext({ accent: undefined, uiAccent: "#9D4CDD", nhlTheme: false, badges: DEFAULT_BADGES, nudges: { jersey: 0, captain: 0, position: 0 }, badgeStyle: "team", boxStyle: "dark", boxShape: "rounded", boxSize: "md", boxOpacity: 85, jerseySize: "md", positionSize: "md", jerseyHash: false, liveIdentity: false });

// Uniform section label — a full-width top header bar (default, matching the
// standings-grade look) OR a fixed-width left team-colour tab. Words stack
// centred (e.g. "1ST" / "LINE").
function SectionLabel({ label, color, op = 100, mode = "top" }) {
    const { nhlTheme } = React.useContext(BadgeCtx);
    const words = String(label).split(" ");
    if (nhlTheme) {
        // Clean broadcast/analytics label — left-aligned, hairline underline, accent tick.
        if (mode === "side") {
            return (
                <div className="shrink-0 self-stretch flex flex-col items-center justify-center gap-0.5 w-[44px] border-r-2 pr-1" style={{ borderColor: withA(color, 60) }} data-testid={`new-lineup-rowtab-${label}`}>
                    {words.map((wd, i) => <span key={i} className="text-[9px] font-black uppercase tracking-[0.12em] text-white/55 text-center leading-tight">{wd}</span>)}
                </div>
            );
        }
        return (
            <div className="w-full flex items-center gap-2 pb-1 border-b border-white/10" data-testid={`new-lineup-rowtab-${label}`}>
                <span className="h-3 w-[3px] rounded-sm" style={{ background: color }} />
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white/60 leading-none">{label}</span>
            </div>
        );
    }
    if (mode === "side") {
        return (
            <div className="shrink-0 self-stretch flex flex-col items-center justify-center rounded-lg px-1 py-1"
                style={{ background: withA(color, op), border: `1px solid ${withA(color, Math.min(100, op + 12))}`, width: 58 }} data-testid={`new-lineup-rowtab-${label}`}>
                {words.map((wd, i) => <span key={i} className="font-heading text-[10px] font-black uppercase tracking-wider text-white text-center leading-tight" style={{ textShadow: "0 1px 3px rgba(0,0,0,.55)" }}>{wd}</span>)}
            </div>
        );
    }
    return (
        <div className="w-full rounded-lg py-1 px-2 flex items-center justify-center leading-none" style={{ background: withA(color, op), border: `1px solid ${withA(color, Math.min(100, op + 12))}` }} data-testid={`new-lineup-rowtab-${label}`}>
            <span className="font-heading text-[10px] font-black uppercase tracking-[0.16em] text-white text-center leading-tight" style={{ textShadow: "0 1px 3px rgba(0,0,0,.5)" }}>{label}</span>
        </div>
    );
}

// ---------- read-only pieces ----------
function Pill({ slot, color, tone, posDefault, playerMap, sz, op = 100, cardW, cardH }) {
    const b = React.useContext(BadgeCtx);
    return (
        <div className="px-1 py-1 flex items-center justify-center" data-testid="new-lineup-pill">
            <Slot slot={slot} posDefault={posDefault} tone={tone} playerMap={playerMap} lay={{ abbreviate: true }} sz={sz} cardW={cardW} cardH={cardH} accent={b.accent} badges={b.badges} nudges={b.nudges} badgeStyle={b.badgeStyle} boxStyle={tone === "g" && b.isNhl ? "team" : b.boxStyle} boxShape={b.boxShape} boxSize={b.boxSize} boxOpacity={b.boxOpacity} jerseySize={b.jerseySize} positionSize={b.positionSize} jerseyHash={b.jerseyHash} liveIdentity={b.liveIdentity} prefFirst={!b.isNhl} />
        </div>
    );
}

// Bold formation label — ordinal + word both bold white (word slightly dimmed only for kerning contrast, matching the '1ST' weight).
function FancyLabel({ text }) {
    const [ord, ...rest] = String(text).split(" ");
    return (
        <div className="text-center mb-1 leading-none" data-testid={`new-lineup-label-${text}`}>
            <span className="font-heading text-base font-black italic tracking-tight text-white" style={{ textShadow: "0 1px 6px rgba(0,0,0,.5)" }}>{ord}</span>
            {rest.length > 0 && <span className="font-heading text-base font-black italic tracking-tight text-white ml-1.5" style={{ textShadow: "0 1px 6px rgba(0,0,0,.5)" }}>{rest.join(" ")}</span>}
        </div>
    );
}

function FRow({ label, slots, cols, color, tone, posDefaults, playerMap, sz, op = 100, jContent = "center", gap = 6, cardW, cardH }) {
    return (
        <div className="mb-1" data-testid={`new-lineup-row-${label}`}>
            {label && <FancyLabel text={label} />}
            <div className="flex flex-nowrap items-start" style={{ gap: Math.max(0, gap), justifyContent: jContent }}>
                {Array.from({ length: cols }, (_, i) => <Pill key={i} slot={slots[i]} color={color} tone={tone} posDefault={posDefaults?.[i]} playerMap={playerMap} sz={sz} cardW={cardW} cardH={cardH} op={op} />)}
            </div>
        </div>
    );
}

function SpecialUnit({ label, slots, fwd, color, playerMap, sz }) {
    const F = slots.slice(0, fwd), D = slots.slice(fwd);
    return (
        <div className="mb-2.5" data-testid={`new-lineup-unit-${label}`}>
            <p className="text-[9px] font-black uppercase tracking-wider text-white/40 mb-1">{label}</p>
            <FRow label="" slots={F} cols={Math.max(1, F.length)} color={color} playerMap={playerMap} sz={sz} />
            <FRow label="" slots={D} cols={Math.max(1, D.length)} color={color} playerMap={playerMap} sz={sz} />
        </div>
    );
}

// ---------- edit-mode pieces ----------
// Pool chip (compact — lives in the Available Players control area). Drag OR
// shift-click to auto-place. No remove cross.
function PoolChip({ p, name, tag, dndProps, sz, onShiftAdd, onCtrlAdd, onTap }) {
    const b = React.useContext(BadgeCtx);
    return (
        <div {...(dndProps || {})} onClickCapture={(e) => {
            if (e.shiftKey) { e.preventDefault(); e.stopPropagation(); onShiftAdd && onShiftAdd(); }
            else if ((e.ctrlKey || e.metaKey) && onCtrlAdd) { e.preventDefault(); e.stopPropagation(); onCtrlAdd(); }
            else if (onTap) { e.preventDefault(); e.stopPropagation(); onTap(); }
        }}
            className="relative flex flex-col items-center select-none cursor-grab active:cursor-grabbing" title={onTap ? `${name} — tap to assign to a slot` : `${name} — drag or shift-click to add · ctrl+click → selected PP/PK unit`}>
            <Slot slot={{ player_id: p.id, player_name: name }} posDefault={tag} playerMap={{ [p.id]: p }} lay={{ abbreviate: true }} sz={sz} noLink
                accent={b.accent} badges={b.badges} nudges={b.nudges} badgeStyle={b.badgeStyle} jerseySize={b.jerseySize} positionSize={b.positionSize} jerseyHash={b.jerseyHash} />
        </div>
    );
}

// A board slot in EDIT mode. Renders the EXACT read-only pill (filled OR blank)
// — identical to public — just wrapped as a drop target / drag source.
// Shift-click removes the player.
function EditSlot({ dndId, val, color, posDefault, tone, playerMap, sz, onShiftRemove, onCaptain, op = 100, cardW, cardH }) {
    const b = React.useContext(BadgeCtx);
    const pid = val?.player_id;
    const cap = String(val?.captain || "").toUpperCase();
    const base = (
        <div className="px-1 py-1 flex items-center justify-center rounded-[14px]" data-testid="new-lineup-pill">
            <Slot slot={val} posDefault={posDefault} tone={tone} playerMap={playerMap} lay={{ abbreviate: true }} sz={sz} cardW={cardW} cardH={cardH} accent={b.accent} badges={b.badges} nudges={b.nudges} badgeStyle={b.badgeStyle} boxStyle={tone === "g" && b.isNhl ? "team" : b.boxStyle} boxShape={b.boxShape} boxSize={b.boxSize} boxOpacity={b.boxOpacity} jerseySize={b.jerseySize} positionSize={b.positionSize} jerseyHash={b.jerseyHash} liveIdentity={b.liveIdentity} prefFirst={!b.isNhl} />
        </div>
    );
    return (
        <Droppable droppableId={dndId} type="PLAYER">
            {(prov, snap) => (
                <div ref={prov.innerRef} {...prov.droppableProps} className="group relative rounded-[14px]" style={snap.isDraggingOver ? { boxShadow: `0 0 0 2px ${b.uiAccent || "#9D4CDD"}` } : undefined}
                    data-testid={`new-lineup-edit-slot-${dndId.replace(/:/g, "-")}`}>
                    {pid && onCaptain && (
                        <button type="button" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCaptain(); }}
                            className={`absolute -top-1.5 left-1/2 -translate-x-1/2 z-20 h-4 px-1.5 rounded-full text-[9px] font-black leading-none border shadow flex items-center justify-center transition-opacity opacity-0 group-hover:opacity-100`}
                            style={{ background: cap === "C" ? "#F6C453" : cap === "A" ? "#0A0B10" : "rgba(10,11,16,.9)", color: cap === "C" ? "#0A0B10" : "#E2E8F0", borderColor: cap === "C" ? "#F6C453" : "rgba(255,255,255,.35)" }}
                            title="Set Captain (C) / Alternate (A)" data-testid={`new-lineup-captain-toggle-${dndId.replace(/:/g, "-")}`}>{cap || "C/A"}</button>
                    )}
                    {pid ? (
                        <Draggable draggableId={dndId} index={0}>
                            {(pr, snap) => (
                                <>
                                    {withPortal(
                                        <div ref={pr.innerRef} {...pr.draggableProps} style={zoomDragStyle(pr.draggableProps.style)} {...pr.dragHandleProps} className="cursor-grab active:cursor-grabbing"
                                            onClickCapture={(e) => { e.preventDefault(); e.stopPropagation(); if (e.shiftKey && onShiftRemove) onShiftRemove(); }}>
                                            {base}
                                        </div>, snap.isDragging)}
                                    {snap.isDragging && (
                                        <div className="opacity-30 pointer-events-none rounded-[14px] ring-2 ring-[#9D4CDD]" data-testid={`new-lineup-drag-ghost-${dndId.replace(/:/g, "-")}`}>
                                            {base}
                                        </div>
                                    )}
                                </>
                            )}
                        </Draggable>
                    ) : base}
                    <div style={{ display: "none" }}>{prov.placeholder}</div>
                </div>
            )}
        </Droppable>
    );
}

// A reserve/shootout section in EDIT mode — renders IDENTICALLY to public
// (grid of pills, or numbered rows) but each item is draggable + the section is
// a drop target. Shift-click an item to remove it.
function EditList({ field, items, color, playerMap, sz, onShiftRemove, numbered, op = 100, gap = 6, cardW, cardH }) {
    const b = React.useContext(BadgeCtx);
    const slotProps = { accent: b.accent, badges: b.badges, nudges: b.nudges, badgeStyle: b.badgeStyle, boxStyle: b.boxStyle, boxShape: b.boxShape, boxSize: b.boxSize, boxOpacity: b.boxOpacity, jerseySize: b.jerseySize, positionSize: b.positionSize, jerseyHash: b.jerseyHash };
    return (
        <Droppable droppableId={field} type="PLAYER">
            {(prov, snap) => (
                <div ref={prov.innerRef} {...prov.droppableProps} style={snap.isDraggingOver ? { boxShadow: `inset 0 0 0 1px ${b.uiAccent || "#9D4CDD"}`, background: `${b.uiAccent || "#9D4CDD"}0d` } : undefined} className="rounded-md" data-testid={`new-lineup-edit-${field}`}>
                    {items.length === 0 && !snap.isDraggingOver
                        ? (numbered ? <p className="text-[10px] text-white/25 italic">Auto order — none set</p> : <p className="text-[9px] text-white/20 italic">—</p>)
                        : (
                            <div className={numbered ? "space-y-1.5" : "flex flex-col"} style={numbered ? undefined : { gap }}>
                                {items.map((it, i) => (
                                    <Draggable key={`${field}-${i}-${it.player_id}`} draggableId={`${field}:${i}`} index={i}>
                                        {(pr, snap) => withPortal(
                                            <div ref={pr.innerRef} {...pr.draggableProps} style={zoomDragStyle(pr.draggableProps.style)} {...pr.dragHandleProps} className="cursor-grab active:cursor-grabbing"
                                                onClickCapture={(e) => { e.preventDefault(); e.stopPropagation(); if (e.shiftKey) onShiftRemove(i); }}>
                                                {numbered ? (
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="w-4 text-center font-black font-mono-stat text-[#B366F0] text-xs">{i + 1}</span>
                                                        <div className="flex-1"><div className="px-1 py-1 flex items-center justify-center"><Slot slot={it} playerMap={playerMap} lay={{ abbreviate: true }} sz={sz} {...slotProps} /></div></div>
                                                    </div>
                                                ) : (
                                                    <div className="px-1 py-1 flex items-center justify-center"><Slot slot={it} playerMap={playerMap} lay={{ abbreviate: true }} sz={sz} cardW={cardW} cardH={cardH} {...slotProps} /></div>
                                                )}
                                            </div>, snap.isDragging)}
                                    </Draggable>
                                ))}
                            </div>
                        )}
                    {prov.placeholder}
                </div>
            )}
        </Droppable>
    );
}

// ---------- Built-in on-screen keyboard (letters + space + enter + backspace) ----------
const KB_ROWS = [
    ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p"],
    ["a", "s", "d", "f", "g", "h", "j", "k", "l"],
    ["z", "x", "c", "v", "b", "n", "m"],
];
function KeyboardPad({ onKey, accent = "#9D4CDD" }) {
    const Key = ({ label, flex, testId, onClick, strong }) => (
        <button type="button" data-testid={testId} onClick={onClick}
            onMouseDown={(e) => e.preventDefault()}
            style={{ flex: flex || "1 1 0", minWidth: 0, borderColor: strong ? `${accent}66` : undefined }}
            className="h-11 rounded-md border border-white/12 bg-white/[0.05] text-white text-[13px] font-bold uppercase flex items-center justify-center active:brightness-150 transition-[filter] select-none">
            {label}
        </button>
    );
    return (
        <div className="mt-2 rounded-xl border border-white/10 bg-[#0A0B10] p-2 flex flex-col gap-1.5" data-testid="quicklines-keyboard">
            {KB_ROWS.map((row, ri) => (
                <div key={ri} className="flex gap-1.5">
                    {ri === 1 && <div style={{ flex: "0 0 4%" }} />}
                    {ri === 2 && <div style={{ flex: "0 0 7%" }} />}
                    {row.map((k) => <Key key={k} label={k} testId={`kb-key-${k}`} onClick={() => onKey(k)} />)}
                    {ri === 2 && <Key label="⌫" flex="1 1 16%" testId="kb-key-back" strong onClick={() => onKey("\b")} />}
                    {ri === 1 && <div style={{ flex: "0 0 4%" }} />}
                </div>
            ))}
            <div className="flex gap-1.5">
                <Key label="Enter" flex="1 1 26%" testId="kb-key-enter" strong onClick={() => onKey("\n")} />
                <Key label="Space" flex="1 1 48%" testId="kb-key-space" onClick={() => onKey(" ")} />
                <Key label="Clear" flex="1 1 26%" testId="kb-key-clear" strong onClick={() => onKey("__clear__")} />
            </div>
        </div>
    );
}

// ---------- Quick Lines: keyboard-first entry ----------
// Tab through every slot (Line 1 LW → C → RW → … → D pairs → goalies), type a
// few letters, Enter/Tab accepts the top match and jumps to the next slot. A
// fast alternative to drag-and-drop — writes into the SAME draft & pool.
function QuickLines({ slots, players, valueOf, onPick, onClear, onBatch = null, accent = "#9D4CDD" }) {
    const refs = React.useRef([]);
    const [active, setActive] = React.useState(-1);
    const [q, setQ] = React.useState("");
    const [hi, setHi] = React.useState(0);
    const placed = React.useMemo(() => {
        const s = new Set();
        slots.forEach((sl) => { const v = valueOf(sl.key); if (v && v.player_id) s.add(String(v.player_id)); });
        return s;
    }, [slots, valueOf]);
    const posRank = (p, want) => {
        const pos = String(p.position || "").toUpperCase();
        if (pos === want) return 3;
        if (want === "G") return pos.startsWith("G") ? 3 : 0;
        const fwd = ["LW", "C", "RW", "L", "R", "F"], def = ["LD", "RD", "D"];
        if (["LW", "C", "RW"].includes(want) && fwd.includes(pos)) return 2;
        if (["LD", "RD"].includes(want) && def.includes(pos)) return 2;
        return 0;
    };
    const matchesFor = (row) => {
        const term = q.trim().toLowerCase();
        const want = slots[row].pos;
        // Show the WHOLE roster (placed players included) so you can type any name
        // and reassign them — picking relocates them out of their old slot. Unplaced
        // players and position-matches float to the top.
        let list = players || [];
        if (term) list = list.filter((p) => (p.name || "").toLowerCase().includes(term));
        list = [...list].sort((a, b) =>
            ((placed.has(String(a.id)) ? 1 : 0) - (placed.has(String(b.id)) ? 1 : 0))
            || (posRank(b, want) - posRank(a, want))
            || (a.name || "").localeCompare(b.name || ""));
        return list.slice(0, 8);
    };
    const focusRow = (i) => { const el = refs.current[i]; if (el) el.focus(); };
    const openRow = (i) => { setActive(i); setQ(""); setHi(0); };
    const pick = (row, p) => {
        if (p) onPick(slots[row].key, p);
        setQ(""); setHi(0);
        const next = row + 1;
        if (next < slots.length) { setActive(next); setTimeout(() => focusRow(next), 0); }
        else { setActive(-1); if (refs.current[row]) refs.current[row].blur(); }
    };
    const onKey = (row, e) => {
        const ms = matchesFor(row);
        if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(ms.length - 1, h + 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(0, h - 1)); }
        else if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pick(row, ms[hi] || ms[0] || null); }
        else if (e.key === "Backspace" && !q) { const cur = valueOf(slots[row].key); if (cur && cur.player_id) { e.preventDefault(); onClear(slots[row].key); } }
        else if (e.key === "Escape") { e.preventDefault(); setActive(-1); if (refs.current[row]) refs.current[row].blur(); }
    };
    const [paste, setPaste] = React.useState("");
    const [pasteOpen, setPasteOpen] = React.useState(false);
    const [pasteMsg, setPasteMsg] = React.useState("");
    const [pasteWarn, setPasteWarn] = React.useState(false);
    // Built-in on-screen keyboard: default ON for touch/phone widths, OFF (device
    // keyboard) on desktop. A toggle lets you switch either way.
    const [deviceKb, setDeviceKb] = React.useState(() => (typeof window !== "undefined" && window.innerWidth >= 768));
    const kbKey = (k) => {
        if (k === "\b") setPaste((p) => p.slice(0, -1));
        else if (k === "__clear__") setPaste("");
        else setPaste((p) => p + k);
    };
    // "Cheat" paste → fill the lines from a text block. One unit per line:
    //   3 names = a forward line, 2 = a D pair, 1 = a goalie (IGNORED — the
    //   roster auto-fills goalies). "Extra:/Scratch:" lines → scratches/reserves.
    // Names survive spaces (two-word surnames like "Eriksson Ek", first initials
    // like "N Foligno"); matching is accent-insensitive, position-aware (so two
    // Elias Petterssons land in the right F/D slot) and initial-aware.
    const applyPaste = () => {
        const pool = players || [];
        const strip = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const words = (s) => strip(s).replace(/[^a-z\s]/g, " ").split(/\s+/).filter(Boolean);
        const isG = (p) => /^g/i.test(String(p.position || ""));
        const posGroup = (p) => { const q = String(p.position || "").toUpperCase(); if (q.startsWith("G")) return "G"; if (q.startsWith("D") || q === "LD" || q === "RD") return "D"; return "F"; };
        const slotGroup = (pos) => { const q = String(pos || "").toUpperCase(); if (q === "G") return "G"; if (q === "LD" || q === "RD" || q === "D") return "D"; return "F"; };
        const matchPlayer = (token, slotPos, used, goalie = false) => {
            const tw = words(token);
            if (!tw.length) return null;
            let initial = null, lastParts = tw;
            if (tw.length >= 2 && tw[0].length === 1) { initial = tw[0]; lastParts = tw.slice(1); } // "N Foligno"
            const lastStr = lastParts.join(" ");
            const lastWord = lastParts[lastParts.length - 1];
            const scored = pool.filter((p) => (goalie ? isG(p) : !isG(p))).map((p) => {
                const pw = words(p.name); if (!pw.length) return null;
                const pLast = pw[pw.length - 1]; const pLast2 = pw.slice(-2).join(" "); const pFirst = pw[0] || "";
                let score = 0;
                if (pLast2 === lastStr) score = 100;            // full two-word surname ("Eriksson Ek")
                else if (pLast === lastStr) score = 92;          // surname exact
                else if (pLast === lastWord) score = 74;         // last word of a multi-word token
                else if (strip(p.name).includes(lastStr)) score = 45; // loose substring
                if (!score) return null;
                if (initial) score += pFirst[0] === initial ? 25 : -60;   // first-initial agreement
                if (slotGroup(slotPos) === posGroup(p)) score += 18;      // position match (two Petterssons)
                if (used.has(String(p.id))) score -= 300;                 // never reuse a placed player
                return { p, score };
            }).filter(Boolean).sort((a, b) => b.score - a.score);
            const best = scored[0];
            return best && best.score > 0 ? best.p : null;
        };
        const splitLine = (line) => {
            if (/[-–—/]/.test(line)) return line.split(/\s*[-–—/]+\s*/).map((s) => s.trim()).filter(Boolean); // hyphen keeps multi-word names
            if (/\t/.test(line) || /\s{2,}/.test(line)) return line.split(/\t+|\s{2,}/).map((s) => s.trim()).filter(Boolean);
            return line.split(/\s+/).map((s) => s.trim()).filter(Boolean);
        };
        const fRows = [], dRows = [], gRows = [], extras = [];
        paste.split(/\n/).forEach((raw) => {
            let line = raw.trim();
            if (!line) return;
            const extra = /^(extras?|scratch(es)?|healthy)\s*[:\-–]/i.test(line);
            if (extra) line = line.replace(/^(extras?|scratch(es)?|healthy)\s*[:\-–]\s*/i, "");
            const toks = splitLine(line);
            if (!toks.length) return;
            if (extra) { extras.push(...toks); return; }
            if (toks.length >= 3) fRows.push(toks.slice(0, 3));
            else if (toks.length === 2) dRows.push(toks);
            else gRows.push(toks[0]); // 1 token = a goalie (starter first, then backup)
        });
        const fSlots = slots.filter((s) => ["LW", "C", "RW"].includes(s.pos));
        const dSlots = slots.filter((s) => ["LD", "RD"].includes(s.pos));
        const gSlots = slots.filter((s) => s.pos === "G");
        let n = 0; const miss = []; const matched = new Set(); const placements = [];
        const place = (tok, slot, pos) => {
            const p = matchPlayer(tok, pos, matched);
            if (p && slot) { placements.push({ key: slot.key, id: p.id, name: p.name }); matched.add(String(p.id)); n++; }
            else if (tok) miss.push(tok);
        };
        fRows.flat().forEach((tok, i) => place(tok, fSlots[i], fSlots[i] ? fSlots[i].pos : "C"));
        dRows.flat().forEach((tok, i) => place(tok, dSlots[i], dSlots[i] ? dSlots[i].pos : "LD"));
        gRows.forEach((tok) => { if (tok) {/* goalie tokens are ignored — goalies come from the Game Centre starter below */} });
        // GOALIES: never trust the pasted text — fill the goalie slots from the
        // game-roster STARTER (Game Centre starting goalie) first, then the backup.
        const poolGoalies = pool.filter(isG).filter((p) => !matched.has(String(p.id)))
            .sort((a, b) => ((b.starter ? 1 : 0) - (a.starter ? 1 : 0)) || ((Number(a.jersey) || 99) - (Number(b.jersey) || 99)));
        gSlots.forEach((slot, i) => {
            const g = poolGoalies[i];
            if (g && slot) { placements.push({ key: slot.key, id: g.id, name: g.name }); matched.add(String(g.id)); n++; }
        });
        extras.forEach((tok) => {
            const p = matchPlayer(tok, "", matched) || matchPlayer(tok, "G", matched, true);
            if (p) { placements.push({ key: "__reserve__", id: p.id, name: p.name }); matched.add(String(p.id)); n++; }
            else if (tok) miss.push(tok);
        });
        // Commit ALL placements in ONE update (a per-pick loop would clone the same
        // stale draft each time and only the last pick would survive).
        if (onBatch) onBatch(placements);
        else placements.forEach((pl) => pl.key !== "__reserve__" && onPick(pl.key, { id: pl.id, name: pl.name }));
        // Flag discrepancies vs the game roster. Skaters flagged if left out;
        // goalies are NOT flagged as "missing" (roster/boxscore auto-fills them).
        const missing = pool.filter((p) => !isG(p) && !matched.has(String(p.id))).map((p) => p.name);
        const parts = [`Placed ${n}`];
        if (miss.length) parts.push(`⚠ not on game roster: ${miss.join(", ")}`);
        if (missing.length) parts.push(`⚠ on roster but missing from source: ${missing.slice(0, 15).join(", ")}${missing.length > 15 ? "…" : ""}`);
        setPasteMsg(parts.join("   ·   "));
        setPasteWarn(miss.length > 0 || missing.length > 0);
    };
    return (
        <div className="rounded-2xl bg-[#0B0C13] border border-white/10 p-4" data-testid="new-lineup-quicklines">
            <div className="flex items-center gap-2 mb-3">
                <ClipboardList className="h-4 w-4" style={{ color: accent }} />
                <h4 className="font-heading text-sm font-black uppercase tracking-wider text-white">Paste Lines</h4>
                <button type="button" onClick={() => setDeviceKb((v) => !v)} data-testid="quicklines-kb-toggle"
                    title={deviceKb ? "Switch to the built-in keyboard" : "Switch to your device keyboard"}
                    style={!deviceKb ? { background: accent, borderColor: accent, color: "#0A0B10" } : { borderColor: "rgba(255,255,255,0.25)" }}
                    className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[10px] font-black uppercase tracking-wider text-white">
                    <Keyboard className="h-3.5 w-3.5" /> {deviceKb ? "Device Keys" : "Built-in Keys"}
                </button>
            </div>
            <div className="rounded-xl border border-white/10 bg-[#0A0B10] p-3" data-testid="quicklines-paste-box">
                <div className="text-[10px] font-bold text-white/45 mb-1.5 normal-case">One unit per line — forwards (3 names), D pairs (2 names). Separate names with spaces, tabs or hyphens. <span className="text-white/70">"Extra:" lines → scratches.</span> Goalies auto-fill from the Game Centre starting goalie — goalie lines in your paste are ignored. Matched to the game roster by last name.</div>
                <textarea value={paste} onChange={(e) => setPaste(e.target.value)} rows={deviceKb ? 12 : 6} data-testid="quicklines-paste-input"
                    readOnly={!deviceKb} inputMode={deviceKb ? undefined : "none"}
                    placeholder={"Knies-Fantilli-Johnson\nNichushkin-Monahan-Garland\nSillinger-Coyle-Olivier\nVoronkov-Heinen-Lomberg\n\nWerenski-Mateychuk\nSoucy-Andrae\nChristiansen-Gudbranson\n\nGreaves\nTalbot"}
                    className="w-full rounded-lg border border-white/12 bg-[#0E0F16] px-2.5 py-2 text-xs text-white placeholder:text-white/25 focus:outline-none focus:border-white/30 font-mono leading-relaxed" />
                {!deviceKb && <KeyboardPad onKey={kbKey} accent={accent} />}
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <button type="button" onClick={applyPaste} disabled={!paste.trim()} data-testid="quicklines-paste-apply"
                        style={{ background: accent }} className="px-3 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-wider text-black disabled:opacity-40">Fill Lines</button>
                    {pasteMsg && <span className={`text-[11px] ${pasteWarn ? "text-amber-300 font-bold" : "text-white/60"}`} data-testid="quicklines-paste-msg">{pasteMsg}</span>}
                </div>
            </div>
        </div>
    );
}

// ---------- per-widget admin header (reorder / colour / size) ----------
function WidgetHeader({ w, admin, team, color, op = 100, isFirst, isLast, onMove, onEdit, editing, onChange, onClose, onSave, fill = false }) {
    const { nhlTheme } = React.useContext(BadgeCtx);
    const St = ({ val, set, title }) => (
        <div className="flex items-center gap-0.5" title={title}>
            <button type="button" onClick={() => set(val - 2)} className="h-4 w-4 rounded bg-white/10 hover:bg-white/20 text-white/70 text-[10px] leading-none">−</button>
            <span className="w-5 text-center text-[9px] font-mono-stat text-white/60">{val}</span>
            <button type="button" onClick={() => set(val + 2)} className="h-4 w-4 rounded bg-white/10 hover:bg-white/20 text-white/70 text-[10px] leading-none">+</button>
        </div>
    );
    if (nhlTheme) {
        return (
            <div className="relative">
                <div className="w-full px-3 py-2 flex items-center gap-2.5 border-b border-white/10 bg-white/[0.05]" data-testid={`new-lineup-header-${w.key}`}>
                    <span className="h-4 w-[3px] rounded-sm shrink-0" style={{ background: color }} />
                    <h3 className="text-[11px] font-black uppercase tracking-[0.22em] text-white/90 leading-none truncate">{w.title}</h3>
                    {admin && (
                        <div className="ml-auto flex items-center gap-0.5">
                            <button onClick={() => onMove(-1)} disabled={isFirst} className="p-0.5 rounded hover:bg-white/10 disabled:opacity-25" title="Move left" data-testid={`new-lineup-move-left-${w.key}`}><ChevronLeft className="w-3.5 h-3.5 text-white/70" /></button>
                            <button onClick={() => onMove(1)} disabled={isLast} className="p-0.5 rounded hover:bg-white/10 disabled:opacity-25" title="Move right" data-testid={`new-lineup-move-right-${w.key}`}><ChevronRight className="w-3.5 h-3.5 text-white/70" /></button>
                        </div>
                    )}
                </div>
            </div>
        );
    }
    return (
        <div className="relative">
            <div className={`relative rounded-lg ${fill ? "w-full" : "w-[170px]"} max-w-full mx-auto px-3 py-1.5 flex items-center justify-center`} style={{ background: withA(color || "#9D4CDD", op), border: `1px solid ${withA(color || "#9D4CDD", Math.min(100, op + 12))}` }} data-testid={`new-lineup-header-${w.key}`}>
                <h3 className="text-[11px] font-black uppercase tracking-[0.16em] text-white text-center leading-tight truncate" style={{ textShadow: "0 1px 4px rgba(0,0,0,.55)" }}>{w.title}</h3>
                {admin && (
                    <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
                        <button onClick={() => onMove(-1)} disabled={isFirst} className="p-0.5 rounded hover:bg-black/25 disabled:opacity-25" title="Move left" data-testid={`new-lineup-move-left-${w.key}`}><ChevronLeft className="w-3.5 h-3.5 text-white" /></button>
                        <button onClick={() => onMove(1)} disabled={isLast} className="p-0.5 rounded hover:bg-black/25 disabled:opacity-25" title="Move right" data-testid={`new-lineup-move-right-${w.key}`}><ChevronRight className="w-3.5 h-3.5 text-white" /></button>
                        <button onClick={onEdit} className={`p-0.5 rounded hover:bg-black/25 ${editing ? "bg-black/40" : ""}`} title="Edit widget" data-testid={`new-lineup-edit-${w.key}`}><Settings2 className="w-3.5 h-3.5 text-white" /></button>
                    </div>
                )}
            </div>
            {admin && editing && (
                <div className="absolute left-0 top-full z-40 mt-1 w-[380px] max-w-[92vw] rounded-lg bg-[#12141D] border border-[#9D4CDD]/40 p-2.5 space-y-2 shadow-2xl shadow-black/60" data-testid={`new-lineup-options-${w.key}`}>
                    <input value={w.title} onChange={(e) => onChange({ title: e.target.value })} placeholder="Widget name"
                        className="w-full bg-[#0A0B10] border border-white/15 rounded px-2 py-1 text-xs text-white" data-testid={`new-lineup-rename-${w.key}`} />
                    <div>
                        <p className="text-[9px] uppercase tracking-wider text-white/40 font-bold mb-1">Title colour</p>
                        <div className="flex flex-wrap items-center gap-1">
                            {COLOR_OPTS.map(([k, l]) => (
                                <button key={k} onClick={() => onChange({ color: k })} data-testid={`new-lineup-title-color-${w.key}-${k}`}
                                    style={(w.color || "primary") === k ? { boxShadow: `0 0 0 2px ${resolveColor(k, team, w.custom)}` } : {}}
                                    className="h-5 w-5 rounded border border-white/20" title={l}>
                                    <span className="block h-full w-full rounded" style={{ background: resolveColor(k, team, w.custom) }} />
                                </button>
                            ))}
                            {w.color === "custom" && (
                                <input type="color" value={w.custom || "#9D4CDD"} onChange={(e) => onChange({ custom: e.target.value })}
                                    data-testid={`new-lineup-title-custom-${w.key}`} className="h-6 w-8 rounded bg-transparent border border-white/20 cursor-pointer" />
                            )}
                        </div>
                    </div>
                    <div>
                        <p className="text-[9px] uppercase tracking-wider text-white/40 font-bold mb-1">Sections — rename &amp; colour</p>
                        <div className="space-y-1.5">
                            {(WIDGET_SECTIONS[w.key] || []).map(([fld, lab]) => {
                                const cur = (w.section_colors || {})[fld] || w.color || "primary";
                                return (
                                    <div key={fld} className="flex items-center gap-1.5">
                                        <input value={(w.section_titles || {})[fld] ?? lab} onChange={(e) => onChange({ section_titles: { ...(w.section_titles || {}), [fld]: e.target.value } })}
                                            data-testid={`new-lineup-section-rename-${w.key}-${fld}`} title={`Rename ${lab}`}
                                            className="w-24 shrink-0 bg-[#0A0B10] border border-white/15 rounded px-1.5 py-0.5 text-[10px] font-bold text-white" />
                                        <div className="flex flex-wrap gap-1">
                                            {COLOR_OPTS.filter(([k]) => k !== "custom").map(([k, l]) => (
                                                <button key={k} onClick={() => onChange({ section_colors: { ...(w.section_colors || {}), [fld]: k } })} data-testid={`new-lineup-section-color-${w.key}-${fld}-${k}`}
                                                    style={cur === k ? { boxShadow: `0 0 0 2px ${resolveColor(k, team, w.custom)}` } : {}}
                                                    className={`h-5 w-5 rounded border border-white/20`}
                                                    title={l}><span className="block h-full w-full rounded" style={{ background: resolveColor(k, team, w.custom) }} /></button>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div>
                        <p className="text-[9px] uppercase tracking-wider text-white/40 font-bold mb-1">Spacing (uniform — border, label, players, rows)</p>
                        <div className="flex items-center gap-2" data-testid={`new-lineup-spacing-${w.key}`}>
                            <St val={Number(w.spacing?.pad ?? 8)} set={(v) => onChange({ spacing: { ...(w.spacing || {}), pad: Math.max(0, Math.min(40, v)) } })} title="One gap used everywhere in this group" />
                            <span className="text-[9px] text-white/45">px — same gap border → label → player → player → border, and between rows</span>
                        </div>
                    </div>
                    <div>
                        <p className="text-[9px] uppercase tracking-wider text-white/40 font-bold mb-1">Card size &amp; spacing — per row (0 = auto / inherit)</p>
                        <div className="grid gap-x-1 gap-y-1 items-center" style={{ gridTemplateColumns: "1fr auto auto auto auto" }} data-testid={`new-lineup-cardsize-${w.key}`}>
                            <span></span>
                            <span className="text-[8px] text-white/40 uppercase text-center" title="Text size for this row">Text</span>
                            <span className="text-[8px] text-white/40 uppercase text-center" title="Card width px (0 = auto)">W</span>
                            <span className="text-[8px] text-white/40 uppercase text-center" title="Card height px (0 = auto)">H</span>
                            <span className="text-[8px] text-white/40 uppercase text-center" title="Spacing px for THIS section (0 = inherit widget spacing)">Gap</span>
                            {(WIDGET_SECTIONS[w.key] || []).map(([fld, lab]) => {
                                const setDim = (k, v) => onChange({ [k]: { ...(w[k] || {}), [fld]: Math.max(0, Math.min(260, v)) } });
                                return (
                                    <React.Fragment key={fld}>
                                        <span className="text-[9px] text-white/60 font-bold truncate pr-1" title={(w.section_titles || {})[fld] ?? lab}>{(w.section_titles || {})[fld] ?? lab}</span>
                                        <select value={(w.section_text_size || {})[fld] || ""} onChange={(e) => onChange({ section_text_size: { ...(w.section_text_size || {}), [fld]: e.target.value } })}
                                            data-testid={`new-lineup-textsize-${w.key}-${fld}`} className="bg-[#0A0B10] border border-white/15 rounded px-1 py-0.5 text-[9px] text-white">
                                            <option value="">Auto</option><option value="small">S</option><option value="medium">M</option><option value="large">L</option><option value="xl">XL</option>
                                        </select>
                                        <St val={Number((w.section_box_w || {})[fld] ?? 0)} set={(v) => setDim("section_box_w", v)} title="Card width (px, 0=auto)" />
                                        <St val={Number((w.section_box_h || {})[fld] ?? 0)} set={(v) => setDim("section_box_h", v)} title="Card height (px, 0=auto)" />
                                        <St val={Number((w.section_gap || {})[fld] ?? 0)} set={(v) => onChange({ section_gap: { ...(w.section_gap || {}), [fld]: Math.max(0, Math.min(40, v)) } })} title="Spacing for this section (px, 0 = inherit)" testid={`new-lineup-gap-${w.key}-${fld}`} />
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>
                    {w.key === "special" && (
                        <div>
                            <p className="text-[9px] uppercase tracking-wider text-white/40 font-bold mb-1">Toggle button colour</p>
                            <div className="flex flex-wrap items-center gap-1">
                                {COLOR_OPTS.map(([k, l]) => (
                                    <button key={k} onClick={() => onChange({ toggle_color: k })} data-testid={`new-lineup-toggle-color-${k}`}
                                        style={(w.toggle_color || "primary") === k ? { boxShadow: `0 0 0 2px ${resolveColor(k, team, w.toggle_custom)}` } : {}}
                                        className="h-5 w-5 rounded border border-white/20" title={l}>
                                        <span className="block h-full w-full rounded" style={{ background: resolveColor(k, team, w.toggle_custom) }} />
                                    </button>
                                ))}
                                {w.toggle_color === "custom" && (
                                    <input type="color" value={w.toggle_custom || "#9D4CDD"} onChange={(e) => onChange({ toggle_custom: e.target.value })}
                                        data-testid="new-lineup-toggle-custom" className="h-6 w-8 rounded bg-transparent border border-white/20 cursor-pointer" />
                                )}
                            </div>
                        </div>
                    )}
                    <div>
                        <p className="text-[9px] uppercase tracking-wider text-white/40 font-bold mb-1">Default text size (per-row above overrides)</p>
                        <div className="inline-flex rounded border border-white/15 overflow-hidden">
                            {["auto", "small", "medium", "large", "xl"].map((s) => (
                                <button key={s} onClick={() => onChange({ text_size: s })} data-testid={`new-lineup-size-${w.key}-${s}`}
                                    className={`px-2 py-0.5 text-[10px] font-bold capitalize ${(w.text_size || "auto") === s ? "bg-white/20 text-white" : "text-white/45"}`}>{s}</button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <p className="text-[9px] uppercase tracking-wider text-white/40 font-bold mb-1">Fill opacity — {w.opacity ?? 100}% (100% = full colour)</p>
                        <div className="inline-flex rounded border border-white/15 overflow-hidden" data-testid={`new-lineup-opacity-${w.key}`}>
                            {[25, 50, 75, 100].map((o) => (
                                <button key={o} onClick={() => onChange({ opacity: o })} data-testid={`new-lineup-opacity-${w.key}-${o}`}
                                    className={`px-2 py-0.5 text-[10px] font-bold tabular-nums ${(w.opacity ?? 100) === o ? "bg-[#9D4CDD] text-white" : "text-white/45"}`}>{o}%</button>
                            ))}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                        <button onClick={onSave} className="inline-flex items-center gap-1 btn-primary text-[11px]" data-testid={`new-lineup-save-${w.key}`}><Check className="w-3 h-3" /> Save</button>
                        <button onClick={onClose} className="text-[11px] text-white/45 hover:text-white">Close</button>
                    </div>
                </div>
            )}
        </div>
    );
}

const MINW = { lines: 270, pairs: 195, goalies: 118, special: 230, shootout: 110, reserves: 138 };

// League adapter — abstracts the data sources so the SAME board (drag/drop,
// widgets, badges, historic-game picker) can drive PWHL and NHL identically.
// Defaults below = the PWHL contract (unchanged behaviour). NHL passes its own.
export const PWHL_LINEUP_ADAPTER = {
    scheduleUrl: (teamId) => `/pwhl/schedule?team_id=${teamId}`,
    parseFixtures: (data) => data || [],
    sideOf: (game, teamId) => (game.home_team_id === teamId ? "home" : "away"),
    gameSummaryUrl: (gameId) => `/games/${gameId}/summary`,
    readGameLineup: (summary, side) => (summary?.[`${side}_lineup`] || {}),
    readGamePublic: (summary, side) => !!(summary && summary[`${side}_lineup_public`]),
    readPlayerMeta: (summary) => (summary?.player_meta || null),
    rosterPoolUrl: (gameId, side) => `/admin/games/${gameId}/roster-pool?side=${side}`,
    parsePool: (data) => (data?.players || []),
    saveActiveUrl: (teamId) => `/admin/teams/${teamId}/lineup`,
    saveActiveBody: (d) => d,
    activeInvalidateKey: (teamId) => ["team-lineup", teamId],
    saveGameUrl: (gameId) => `/admin/games/${gameId}/lineup`,
    saveGameBody: (side, d) => ({ side, lineup: d }),
    publicUrl: (gameId) => `/admin/games/${gameId}/lineup-public`,
    publicBody: (side, pub) => ({ side, public: pub }),
    supportsPublicToggle: true,
    fromRosterUrl: (gameId) => `/admin/games/${gameId}/lineup/from-roster`,
    gameHref: (game) => `/game/${game.id}`,
    renderPicker: null,        // null → use the default PWHL RosterGamePicker in nav
    autoSelectFirst: false,    // true → auto-select the most recent game (NHL)
};


export function NewLineupBoard({ lineup, team, teamId, playerMap, admin = false, designEdit = false, gameCentre = false, pool = [], initialTarget = "active", lockTarget = false, hoistToNav = false, adapter = PWHL_LINEUP_ADAPTER, uiAccent = "#9D4CDD", nhlTheme = false, forceDevice = null }) {
    const qc = useQueryClient();
    const [openingBusy, setOpeningBusy] = useState(false);
    const [quickLines, setQuickLines] = useState(false);
    const [tapPlayer, setTapPlayer] = useState(null); // mobile tap-to-assign target
    // Layout config (widths, colours, opacity, order, titles) is GLOBAL across
    // every team — one shared board layout. Player assignments stay per-team.
    const { data: serverCfg } = useQuery({ queryKey: ["new-lineup-config"], queryFn: () => api.get(`/new-lineup-config`).then((r) => r.data) });
    const [cfg, setCfg] = useState(null);
    useEffect(() => {
        if (!serverCfg) return;
        const c = { ...serverCfg };
        let ws = Array.isArray(c.widgets) ? [...c.widgets] : [];
        // Migration: goalies became their OWN widget (was a sub-section of pairs).
        // Inject it right after Defenders, carrying over any goalie sub-settings.
        if (ws.length && !ws.some((w) => w.key === "goalies")) {
            const pi = ws.findIndex((w) => w.key === "pairs");
            const p = pi >= 0 ? ws[pi] : {};
            const gw = {
                key: "goalies", span: 1, title: (p.section_titles || {}).goalies || "Goalies",
                color: (p.section_colors || {}).goalies || p.color || "primary",
                custom: p.custom, opacity: p.opacity ?? 100,
                section_titles: { goalies: (p.section_titles || {}).goalies },
                section_box_w: { goalies: (p.section_box_w || {}).goalies },
                section_box_h: { goalies: (p.section_box_h || {}).goalies },
                section_text_size: { goalies: (p.section_text_size || {}).goalies },
            };
            ws.splice(pi >= 0 ? pi + 1 : ws.length, 0, gw);
            c.widgets = ws;
        } else if (ws.length) {
            // Backfill a missing title on an already-saved goalies widget.
            ws = ws.map((w) => (w.key === "goalies" && (!w.title || w.title === "Goaltenders")) ? { ...w, title: "Goalies" } : w);
            c.widgets = ws;
        }
        setCfg(c);
    }, [serverCfg]);
    const saveCfg = useMutation({ mutationFn: (config) => api.put(`/admin/new-lineup-config`, { config }).then((r) => r.data), onSuccess: () => qc.invalidateQueries({ queryKey: ["new-lineup-config"] }) });

    const [editKey, setEditKey] = useState(null);
    // Admin can maintain SEPARATE settings for mobile. `device` = which breakpoint
    // the admin is editing; `vwMobile` = the live viewport for public rendering.
    const [device, setDevice] = useState("desktop");
    const [vwMobile, setVwMobile] = useState(typeof window !== "undefined" && window.innerWidth < 768);
    useEffect(() => {
        const on = () => setVwMobile(window.innerWidth < 768);
        window.addEventListener("resize", on);
        return () => window.removeEventListener("resize", on);
    }, []);
    const [specType, setSpecType] = useState("pp");
    const [specView, setSpecView] = useState({ pp: "5v4", pk: "4v5", ev: "4v4" });
    const [shootPage, setShootPage] = useState(1);
    const setView = (v) => setSpecView((s) => ({ ...s, [specType]: v }));

    // -------- editing state (editing is ALWAYS on when admin — no toggle) --------
    const editing = admin;
    const [target, setTarget] = useState(initialTarget);   // "active" | <gameId>
    // When the board is LOCKED to a specific target (e.g. a game centre), keep the
    // internal target in sync with the prop. Without this, toggling admin on/off
    // while already viewing the board reuses the same component instance and the
    // target stays stuck at its first value → the roster pool never loads.
    useEffect(() => { if (lockTarget) setTarget(initialTarget); }, [lockTarget, initialTarget]);
    const [draft, setDraft] = useState(null);
    const [note, setNote] = useState("");
    // Undo / Redo history for the editable draft (JSON snapshots).
    const histRef = React.useRef({ stack: [], idx: -1, internal: false });
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);

    const { data: fixturesRaw = [] } = useQuery({
        queryKey: ["team-fixtures-lineup", teamId, adapter.scheduleUrl(teamId || "")],
        queryFn: () => api.get(adapter.scheduleUrl(teamId)).then((r) => r.data),
        enabled: !!teamId,
    });
    const fixtures = useMemo(() => adapter.parseFixtures(fixturesRaw) || [], [fixturesRaw, adapter]);
    // NHL (autoSelectFirst): once the game list loads, jump to the most recent
    // game so the board opens on "last game" like the simpler view did.
    useEffect(() => {
        if (!adapter.autoSelectFirst || lockTarget) return;
        if (target === "active" && fixtures.length) setTarget(fixtures[0].id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adapter.autoSelectFirst, fixtures]);
    // When the board is LOCKED to one game (game centre), always resolve a target
    // game — even if it isn't in the team's recent-games list (live/upcoming games
    // often aren't) — so the roster-building pool + draft load. String-compare
    // guards against number/string id mismatches from the schedule.
    const selGame = target === "active" ? null
        : (fixtures.find((f) => f.id === target)
            || fixtures.find((f) => String(f.id) === String(target))
            || (lockTarget ? { id: target } : null));
    const side = selGame ? adapter.sideOf(selGame, teamId) : null;
    const { data: gameSummary } = useQuery({
        queryKey: ["game-summary-edit", adapter.gameSummaryUrl(target || ""), side],
        queryFn: () => api.get(adapter.gameSummaryUrl(target)).then((r) => r.data),
        enabled: !!selGame,
        // NHL: an auto (unconfirmed / last-game / not-yet) lineup follows the live dressed roster — re-check every minute.
        refetchInterval: (q) => (["unconfirmed", "last_game", "none"].includes(q.state.data?.status) ? 60000 : false),
    });
    const { data: gamePool } = useQuery({
        queryKey: ["game-roster-pool", adapter.rosterPoolUrl(target || "", side), side],
        queryFn: () => api.get(adapter.rosterPoolUrl(target, side)).then((r) => r.data),
        enabled: editing && !!selGame && !!side,
    });

    // Load the chosen source into the editable draft.
    const loadRef = React.useRef({ src: "", snap: "" });
    useEffect(() => {
        if (!editing) return;
        // GAME CENTRE: captaincy (C / A) is AUTOMATIC — stamp each placed player's
        // captain letter from THIS game's roster (playerMap.captain) so the badge
        // fills in without manual toggling and persists with the saved lineup.
        const stampCaps = (d) => {
            if (!gameCentre || !d) return d;
            const apply = (s) => { if (s && s.player_id) { const c = String(playerMap?.[s.player_id]?.captain || "").toUpperCase(); if (c === "C" || c === "A") s.captain = c; } };
            (d.forward_lines || []).forEach((l) => (l || []).forEach(apply));
            (d.d_pairs || []).forEach((l) => (l || []).forEach(apply));
            (d.goalies || []).forEach(apply);
            return d;
        };
        // A background refresh never overwrites edits you've made to the same lineup.
        const src = `${target}|${side}`;
        if (loadRef.current.src === src && loadRef.current.snap && draft && JSON.stringify(draft) !== loadRef.current.snap) return;
        const next = target === "active" ? withAutoReserves(hydrate(lineup || {})) : (gameSummary && side) ? stampCaps(hydrate(adapter.readGameLineup(gameSummary, side))) : null;
        if (!next) return;
        loadRef.current = { src, snap: JSON.stringify(next) };
        setDraft(next); setNote("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editing, target, side, lineup ? lineup.updated_at : "", gameSummary ? JSON.stringify(adapter.readGameLineup(gameSummary, side)) : ""]);

    // OWNER only, GAME CENTRE only: one-click populate the OPENING LINEUP — the 6
    // players the roster marks as starters (3 F on line 1, the starting pair, and
    // the starting goalie) — into the editable draft (not saved until Save).
    const loadOpening = async () => {
        if (!selGame || !side) return;
        setOpeningBusy(true);
        try {
            const res = await api.post(adapter.fromRosterUrl(target), { side, source: "opening", preview: true });
            const lu = hydrate(res.data?.lineup || {});
            const apply = (s) => { if (s && s.player_id) { const c = String(playerMap?.[s.player_id]?.captain || "").toUpperCase(); if (c === "C" || c === "A") s.captain = c; } };
            (lu.forward_lines || []).forEach((l) => (l || []).forEach(apply));
            (lu.d_pairs || []).forEach((l) => (l || []).forEach(apply));
            (lu.goalies || []).forEach(apply);
            setDraft(lu);
        } finally { setOpeningBusy(false); }
    };

    // Track draft snapshots for Undo/Redo. Reset the history whenever the source
    // (active vs a specific game) changes so undo can't cross lineups.
    useEffect(() => { histRef.current = { stack: [], idx: -1, internal: false }; setCanUndo(false); setCanRedo(false); }, [target, side]);
    useEffect(() => {
        if (!editing || draft == null) return;
        const h = histRef.current;
        if (h.internal) { h.internal = false; return; }
        const snap = JSON.stringify(draft);
        if (h.stack[h.idx] === snap) return;
        h.stack = h.stack.slice(0, h.idx + 1);
        h.stack.push(snap);
        h.idx = h.stack.length - 1;
        setCanUndo(h.idx > 0);
        setCanRedo(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draft, editing]);
    const undo = () => { const h = histRef.current; if (h.idx <= 0) return; h.idx -= 1; h.internal = true; setDraft(JSON.parse(h.stack[h.idx])); setCanUndo(h.idx > 0); setCanRedo(true); };
    const redo = () => { const h = histRef.current; if (h.idx >= h.stack.length - 1) return; h.idx += 1; h.internal = true; setDraft(JSON.parse(h.stack[h.idx])); setCanRedo(h.idx < h.stack.length - 1); setCanUndo(true); };

    const editPool = useMemo(() => (target === "active" ? (pool || []) : (adapter.parsePool(gamePool) || [])), [target, pool, gamePool, adapter]);
    const lineupGroups = useMemo(() => {
        const groups = {};
        for (const f of fixtures) {
            const season = f.season || "—";
            const stage = f.stage || "regular";
            const key = `${season}||${stage}`;
            (groups[key] = groups[key] || { season, stage, games: [] }).games.push(f);
        }
        return Object.values(groups)
            .map((g) => ({ ...g, games: [...g.games].sort((a, b) => (b.date || "").localeCompare(a.date || "")) }))
            .sort((a, b) => b.season.localeCompare(a.season) || ((STAGE_ORDER[a.stage] ?? 9) - (STAGE_ORDER[b.stage] ?? 9)));
    }, [fixtures]);
    // When viewing a specific GAME, merge that game's per-game player meta
    // (jersey/position/captain + PHOTO + name — from /games/{id}/summary) over
    // the base player map, so historical lineups render faces + numbers even for
    // players no longer on the team's active roster.
    const effMap = useMemo(() => {
        const base = { ...(playerMap || {}) };
        const pm = (selGame && gameSummary && adapter.readPlayerMeta(gameSummary)) || null;
        if (pm) {
            Object.entries(pm).forEach(([id, m]) => {
                base[id] = { id, ...(base[id] || {}), ...Object.fromEntries(Object.entries(m).filter(([, v]) => v != null && v !== "")) };
            });
        }
        return base;
    }, [playerMap, selGame, gameSummary]);
    const byId = useMemo(() => {
        const m = {};
        (editPool || []).forEach((p) => { m[p.id] = p; });
        Object.entries(effMap || {}).forEach(([id, p]) => { if (!m[id]) m[id] = { id, ...p }; });
        return m;
    }, [editPool, effMap]);

    const saveActive = useMutation({
        mutationFn: (d) => api.put(adapter.saveActiveUrl(teamId), adapter.saveActiveBody(d)).then((r) => r.data),
        onSuccess: () => { qc.invalidateQueries({ queryKey: adapter.activeInvalidateKey(teamId) }); setNote("Active lineup saved."); },
    });
    const saveGame = useMutation({
        mutationFn: (d) => api.put(adapter.saveGameUrl(target), adapter.saveGameBody(side, d, target)).then((r) => r.data),
        onSuccess: () => { qc.invalidateQueries({ queryKey: ["game-summary-edit", adapter.gameSummaryUrl(target || ""), side] }); setNote("Game lineup saved."); },
    });
    const setPublic = useMutation({
        mutationFn: (pub) => api.put(adapter.publicUrl(target), adapter.publicBody(side, pub)).then((r) => r.data),
        onSuccess: () => qc.invalidateQueries({ queryKey: ["game-summary-edit", adapter.gameSummaryUrl(target || ""), side] }),
    });
    // PWHL active lineup: every rostered player not placed anywhere goes to Healthy Scratch.
    const withAutoReserves = (d) => {
        if (adapter !== PWHL_LINEUP_ADAPTER || target !== "active" || !d) return d;
        const placed = placedSet(d);
        const extra = (pool || []).filter((p) => p.id && !placed.has(String(p.id)) && !placed.has(p.id))
            .map((p) => ({ label: "Scratch", position: p.position || "", player_id: String(p.id), player_name: p.name || "" }));
        return extra.length ? { ...d, reserves: [...(d.reserves || []), ...extra] } : d;
    };
    const onSave = () => { if (!draft) return; if (target === "active") saveActive.mutate(withAutoReserves(draft)); else saveGame.mutate(draft); };
    const saving = saveActive.isPending || saveGame.isPending;
    const gamePublic = adapter.readGamePublic(gameSummary, side);

    // Hoist the lineup picker + overview into the nav (nav bar 3) on the team
    // Lineups tab. Must run before the "No lineup set" early return below.
    const { setNav } = useGameNav();
    useEffect(() => {
        if (!hoistToNav) return undefined;
        setNav({
            content: (
                <div className="hidden lg:flex flex-col items-center gap-2 w-full" data-testid="lineup-controls-desktop">
                    {lockTarget ? (
                        <div className="flex items-center justify-center gap-2 w-full">
                            <NavPageTitle page="Lineups" testId="lineup-league-title" />
                        </div>
                    ) : adapter.renderPicker ? (
                        adapter.renderPicker({ teamId, target, onTarget: setTarget, fixtures, team })
                    ) : (
                        <RosterGamePicker teamId={teamId} target={target} onTarget={setTarget} titlePage="Lineups" activeLabel="Active Lineup" availType="lineup" />
                    )}
                </div>
            ),
        });
        return () => setNav(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hoistToNav, target, lineupGroups.length, lockTarget]);

    if (!lineup && !editing) return <div className="rounded-2xl border border-white/10 bg-[#11141F] p-8 text-center text-white/40" data-testid="new-lineup-empty">No lineup set.</div>;
    const widgets = cfg?.widgets || [];

    // Effective (device-scoped) config used for RENDERING. Admin previews the
    // breakpoint being edited (`device`); public follows the live viewport.
    // `forceDevice` (schedule side-lineups) pins the layout; otherwise admins follow
    // the device toggle and the public follows the real viewport width.
    const renderDevice = forceDevice ? forceDevice : (admin ? device : (vwMobile ? "mobile" : "desktop"));
    const eff = renderDevice === "mobile" ? { ...(cfg || {}), ...((cfg && cfg.mobile) || {}) } : (cfg || {});
    // Device-aware option getter/setter for admin controls.
    const dget = (key, dflt) => {
        if (device === "mobile") { const m = cfg?.mobile || {}; return m[key] !== undefined ? m[key] : (cfg?.[key] !== undefined ? cfg[key] : dflt); }
        return cfg?.[key] !== undefined ? cfg[key] : dflt;
    };
    const dset = (key, v) => setCfg((c) => {
        const next = device === "mobile" ? { ...c, mobile: { ...(c.mobile || {}), [key]: v } } : { ...c, [key]: v };
        saveCfg.mutate(next);
        return next;
    });

    // Jersey chip colour = the team's primary colour (dynamic per team).
    const teamAccent = resolveColor("primary", team);
    // Around-the-photo badge placement (corner -> which badge), device-scoped.
    const badgeCorners = eff?.badge_corners || { tl: "jersey", tr: "captain", bc: "position" };
    const badgePos = (() => {
        const m = { ...DEFAULT_BADGES };
        ["jersey", "captain", "position"].forEach((k) => { m[k] = "off"; });
        Object.entries(badgeCorners).forEach(([corner, kind]) => { if (kind && kind !== "none") m[kind] = corner; });
        return m;
    })();
    const editBadgeCorners = (device === "mobile" ? (cfg?.mobile?.badge_corners) : cfg?.badge_corners) || badgeCorners;
    const cornerOf = (kind) => (Object.entries(editBadgeCorners).find(([, k]) => k === kind) || [])[0] || "off";
    const setBadge = (kind, corner) => {
        const cur = { ...editBadgeCorners };
        Object.keys(cur).forEach((cc) => { if (cur[cc] === kind) delete cur[cc]; });
        if (corner && corner !== "off") cur[corner] = kind;
        dset("badge_corners", cur);
    };
    const nudgePx = (key) => (Number(eff?.[key] ?? 3) - 3) * 5;   // 7 steps → -15..+15px
    const badgeNudges = { jersey: nudgePx("jersey_nudge"), captain: nudgePx("captain_nudge"), position: nudgePx("position_nudge") };

    // ---- data views (read-only path uses `lineup` or the chosen game's public
    // lineup; edit path uses the editable `draft`) ----
    const src = editing && draft
        ? draft
        : (selGame && gameSummary ? (adapter.readGameLineup(gameSummary, side) || {}) : withAutoReserves(lineup || {}));
    const su = src.special_units || {};
    const fwdLines = Array.from({ length: 5 }, (_, i) => (src.forward_lines || [])[i] || []);
    const dPairs = Array.from({ length: 4 }, (_, i) => (src.d_pairs || [])[i] || []);
    const goalies = src.goalies || [];
    const shooters = filled(src.shootout_order);
    const shootPages = Math.max(1, Math.ceil(shooters.length / SHOOT_PER_PAGE));
    const shootRows = shooters.slice((shootPage - 1) * SHOOT_PER_PAGE, shootPage * SHOOT_PER_PAGE);
    const reserveSecs = [["Healthy Scratch", "reserves", filled([...(src.reserves || []), ...(su.g3 || [])])], ["IR", "ir", filled(su.ir)], ["LTIR", "ltir", filled(su.ltir)]];

    // A per-game lineup shows on the (public) Lineups page AUTOMATICALLY whenever
    // a lineup record exists for this game with at least ONE player placed into
    // the lineup (i.e. outside the Available Players pool). No manual public/
    // private toggle — placing a player IS the publish action.
    // NHL (autoSelectFirst): always render the formation for the most-recent game
    // even when it's still BLANK, so every team is viewable (not just the one with
    // a posted lineup).
    const hasAnyPlayers = fwdLines.some((l) => l.some(Boolean)) || dPairs.some((p) => p.some(Boolean)) || goalies.some(Boolean);
    if (selGame && !editing && !hasAnyPlayers && !adapter.autoSelectFirst) {
        return <div className="rounded-2xl border border-white/10 bg-[#11141F] p-8 text-center text-white/40" data-testid="new-lineup-none">No lineup available for this game</div>;
    }

    const view = specView[specType];
    const specFwd = STRENGTH[view].fwd, specN = STRENGTH[view].n;
    const unitKey = `${specType}_${view}`;
    const su5 = (k, n) => { const arr = su[k] || []; return Array.from({ length: n }, (_, i) => arr[i]); };

    const patchWidget = (key, patch) => setCfg((c) => ({ ...c, widgets: c.widgets.map((w) => w.key === key ? { ...w, ...patch } : w) }));
    const moveWidget = (key, dir) => setCfg((c) => {
        const ws = [...c.widgets]; const i = ws.findIndex((w) => w.key === key); const j = i + dir;
        if (j < 0 || j >= ws.length) return c;
        [ws[i], ws[j]] = [ws[j], ws[i]]; const next = { ...c, widgets: ws }; saveCfg.mutate(next); return next;
    });

    // ---- drag end ----
    const onDragEnd = ({ source, destination, draggableId }) => {
        if (!destination || !draft) return;
        const s = parseId(draggableId);
        let pid;
        if (s.kind === "pool") pid = s.pid;
        else if (s.kind === "res") pid = (draft.reserves[s.idx] || {}).player_id;
        else if (s.kind === "so") pid = (draft.shootout_order?.[s.idx] || {}).player_id;
        else if (["ir", "ltir", "g3"].includes(s.kind)) pid = ((draft.special_units?.[s.kind] || [])[s.idx] || {}).player_id;
        else if (s.kind === "ev") pid = (getEV(draft, s.key) || {}).player_id;
        else if (s.kind === "su") pid = ((draft.special_units?.[s.unit] || [])[s.idx] || {}).player_id;
        if (!pid) return;
        const p = byId[pid] || { name: pid };
        const val = { player_id: pid, player_name: p.name || "" };
        const d = clone(draft); d.special_units = d.special_units || {};
        const dst = destination.droppableId;

        if (dst === "pool") { clearSource(d, s); return setDraft(d); }
        if (dst === source.droppableId && ["ir", "ltir", "g3", "reserves", "so"].includes(dst)) {
            const arr = dst === "reserves" ? d.reserves : dst === "so" ? d.shootout_order : (d.special_units[dst] || []);
            const [m] = arr.splice(s.idx, 1); arr.splice(destination.index, 0, m); return setDraft(d);
        }
        if (dst === "so") { if (!(d.shootout_order || []).some((x) => x.player_id === pid)) d.shootout_order = [...(d.shootout_order || []), val]; return setDraft(d); }
        if (dst === "reserves") { clearSource(d, s); removeExclusive(d, pid); d.reserves = [...(d.reserves || []), { label: "Scratch", position: posTag(p), player_id: pid, player_name: p.name || "" }]; return setDraft(d); }
        if (["ir", "ltir", "g3"].includes(dst)) { clearSource(d, s); removeExclusive(d, pid); d.special_units[dst] = [...(d.special_units[dst] || []), val]; return setDraft(d); }
        if (dst.startsWith("su:")) {
            const [, unit, idxS] = dst.split(":"); const idx = +idxS;
            d.special_units[unit] = d.special_units[unit] || [];
            while (d.special_units[unit].length <= idx) d.special_units[unit].push({ ...EMPTY });
            const prev = { ...(d.special_units[unit][idx] || EMPTY) };
            d.special_units[unit] = d.special_units[unit].map((x) => (x && x.player_id === pid ? { ...EMPTY } : x));
            d.special_units[unit][idx] = val;
            if (s.kind === "su" && s.unit === unit) d.special_units[unit][s.idx] = prev;
            return setDraft(d);
        }
        if (dst.startsWith("slot:")) {
            const key = dst.slice(5); const prev = { ...getEV(d, key) };
            clearSource(d, s); removeExclusive(d, pid); writeEV(d, key, val);
            if (s.kind === "ev" && s.key !== key && prev.player_id) writeEV(d, s.key, { player_id: prev.player_id, player_name: prev.player_name });
            // Dragged in from Scratches / IR / LTIR / 3rd G → the displaced player takes that spot (never vanishes).
            else if (prev.player_id && prev.player_id !== pid && s.kind === "res") {
                d.reserves.splice(Math.min(s.idx, d.reserves.length), 0, { label: "Scratch", position: posTag(byId[prev.player_id] || prev), player_id: prev.player_id, player_name: prev.player_name || "" });
            } else if (prev.player_id && prev.player_id !== pid && ["ir", "ltir", "g3"].includes(s.kind)) {
                const arr = d.special_units[s.kind] || [];
                arr.splice(Math.min(s.idx, arr.length), 0, { player_id: prev.player_id, player_name: prev.player_name || "" });
                d.special_units[s.kind] = arr;
            }
            return setDraft(d);
        }
    };

    // Shift-click on a placed player → remove back to Available Players.
    const removeById = (dndId) => { const d = clone(draft); d.special_units = d.special_units || {}; clearSource(d, parseId(dndId)); setDraft(d); };
    // Cycle a fixed-slot player's captaincy: none → C → A → none. Enforces
    // exactly 1 captain (C) and at most 3 alternates (A) across the lineup.
    const cycleCaptain = (dndId) => {
        const s = parseId(dndId);
        if (s.kind !== "ev") return;
        const d = clone(draft);
        const allFixed = () => [...d.forward_lines.flat(), ...d.d_pairs.flat(), ...d.goalies].filter((x) => x && x.player_id);
        const cur = getEV(d, s.key) || {};
        let next = cur.captain === "C" ? "A" : cur.captain === "A" ? "" : "C";
        if (next === "C") {
            allFixed().forEach((sl) => { if (String(sl.captain || "").toUpperCase() === "C") sl.captain = ""; });
        } else if (next === "A") {
            const aCount = allFixed().filter((sl) => sl !== cur && String(sl.captain || "").toUpperCase() === "A").length;
            if (aCount >= 3) next = "";
        }
        writeEV(d, s.key, { ...cur, captain: next });
        setDraft(d);
    };
    // Ctrl/Cmd-click on an available player → drop them into the FIRST open slot of
    // the currently-selected special-teams unit (the PP or PK unit shown on screen).
    const sendToSpecialUnit = (pid) => {
        if (!draft) return;
        const p = byId[pid] || { name: pid };
        const val = { player_id: pid, player_name: p.name || "" };
        const d = clone(draft); d.special_units = d.special_units || {};
        const arr = (d.special_units[unitKey] || []).slice();
        while (arr.length < specN) arr.push({ ...EMPTY });
        for (let i = 0; i < arr.length; i++) if (arr[i] && arr[i].player_id === pid) arr[i] = { ...EMPTY };
        let placed = false;
        for (let i = 0; i < specN; i++) if (!arr[i] || !arr[i].player_id) { arr[i] = val; placed = true; break; }
        if (!placed) arr[specN - 1] = val;
        d.special_units[unitKey] = arr;
        setDraft(d);
    };
    // Shift-click on an available player → auto-place into the next open slot
    // that MATCHES the player's position (C→C, LW→LW, LD→LD, …). Goalies overflow
    // to the 3rd-Goalie reserve; skaters overflow to healthy scratch.
    const autoAdd = (pid) => {
        const d = clone(draft); d.special_units = d.special_units || {};
        const p = byId[pid] || { name: pid }; const val = { player_id: pid, player_name: p.name || "" };
        const empty = (s) => !s || !s.player_id;
        const posOf = (x) => String(x || "").toUpperCase();
        // GAME CENTRE: place STRICTLY by the player's PER-GAME roster position
        // category (F / D / G) that this game states — never their global
        // preferred column. So an "F" fills the next open forward slot, a "D" the
        // next open defence slot, a "G" the next open goalie slot.
        if (gameCentre) {
            const gpos = posOf(p.position);
            if (gpos.startsWith("G")) {
                for (let i = 0; i < d.goalies.length; i++) if (empty(d.goalies[i])) { d.goalies[i] = val; return setDraft(d); }
                d.reserves = d.reserves || [];
                if (!d.reserves.some((x) => x.player_id === pid)) d.reserves.push(val);
                return setDraft(d);
            }
            // Placement uses THIS game's roster position (already specific:
            // LW/RW/C/LD/RD). Fall back to all-time master only for a generic F/D.
            const SPECS = ["LW", "RW", "C", "LD", "RD"];
            const spec = SPECS.includes(gpos) ? gpos : (posOf(p.master_position) || posOf(p.pref_position) || gpos);
            const isDef = gpos === "D" || gpos.endsWith("D") || spec === "LD" || spec === "RD";
            if (isDef) {
                const col = spec.startsWith("L") ? 0 : spec.startsWith("R") ? 1 : null;
                if (col !== null) for (let i = 0; i < d.d_pairs.length; i++) if (empty(d.d_pairs[i][col])) { d.d_pairs[i][col] = val; return setDraft(d); }
                for (let i = 0; i < d.d_pairs.length; i++) for (let j = 0; j < d.d_pairs[i].length; j++) if (empty(d.d_pairs[i][j])) { d.d_pairs[i][j] = val; return setDraft(d); }
                d.reserves = [...(d.reserves || []), { label: "Scratch", position: "D", player_id: pid, player_name: p.name || "" }];
                return setDraft(d);
            }
            const fcol = { LW: 0, L: 0, C: 1, RW: 2, R: 2 }[spec];
            if (fcol !== undefined) for (let i = 0; i < d.forward_lines.length; i++) if (empty(d.forward_lines[i][fcol])) { d.forward_lines[i][fcol] = val; return setDraft(d); }
            for (let i = 0; i < d.forward_lines.length; i++) for (let j = 0; j < d.forward_lines[i].length; j++) if (empty(d.forward_lines[i][j])) { d.forward_lines[i][j] = val; return setDraft(d); }
            d.reserves = [...(d.reserves || []), { label: "Scratch", position: "F", player_id: pid, player_name: p.name || "" }];
            return setDraft(d);
        }
        // Resolve the target position: prefer a SPECIFIC position (LW/RW/C/LD/RD)
        // from the player's preferred → primary → secondary, so e.g. a "D" whose
        // preferred is "RD" goes to RD (not the first open D slot), and an "F"
        // whose secondary is a real "LW" is placed at LW.
        const SPEC = new Set(["LW", "RW", "C", "LD", "RD", "L", "R"]);
        const cands = [p.pref_position, p.position, p.secondary_position].map(posOf).filter(Boolean);
        const pos = cands.find((x) => SPEC.has(x)) || cands[0] || "";
        if (pos.startsWith("G")) {
            for (let i = 0; i < d.goalies.length; i++) if (empty(d.goalies[i])) { d.goalies[i] = val; return setDraft(d); }
            // No 3rd-goalie slot any more → an extra goalie goes to Healthy Scratch.
            d.reserves = d.reserves || [];
            if (!d.reserves.some((x) => x.player_id === pid)) d.reserves.push(val);
            return setDraft(d);
        }
        if (pos === "D" || pos.endsWith("D")) {
            const col = pos.startsWith("L") ? 0 : pos.startsWith("R") ? 1 : null;   // LD / RD / D
            if (col !== null) for (let i = 0; i < d.d_pairs.length; i++) if (empty(d.d_pairs[i][col])) { d.d_pairs[i][col] = val; return setDraft(d); }
            for (let i = 0; i < d.d_pairs.length; i++) for (let j = 0; j < d.d_pairs[i].length; j++) if (empty(d.d_pairs[i][j])) { d.d_pairs[i][j] = val; return setDraft(d); }
            d.reserves = [...(d.reserves || []), { label: "Scratch", position: pos || "D", player_id: pid, player_name: p.name || "" }];
            return setDraft(d);
        }
        const fcol = { LW: 0, L: 0, C: 1, RW: 2, R: 2 };   // forward columns are LW / C / RW
        const col = fcol[pos] !== undefined ? fcol[pos] : null;
        if (col !== null) for (let i = 0; i < d.forward_lines.length; i++) if (empty(d.forward_lines[i][col])) { d.forward_lines[i][col] = val; return setDraft(d); }
        for (let i = 0; i < d.forward_lines.length; i++) for (let j = 0; j < d.forward_lines[i].length; j++) if (empty(d.forward_lines[i][j])) { d.forward_lines[i][j] = val; return setDraft(d); }
        d.reserves = [...(d.reserves || []), { label: "Scratch", position: pos || "F", player_id: pid, player_name: p.name || "" }];
        return setDraft(d);
    };

    // Public shootout — rendered as the bottom section INSIDE the Special Teams
    // box (5 numbered slots per page + pager). Auto-order comes from the backend
    // (most recent game with a shootout); admins still edit it as its own widget.
    const shootoutInline = (opOverride) => {
        const soW = widgets.find((x) => x.key === "shootout") || {};
        const c = secColorOf(soW, "so", team);
        // The shootout lives INSIDE the Special Teams box → inherit its opacity so
        // the title matches (the shootout widget itself is hidden/uneditable).
        const op = opOverride != null ? opOverride : (soW.opacity ?? 100);
        const labelStyle = eff?.label_style || "top";
        const title = soW.title || "Shootout";
        const grid = (
            <>
                {shooters.length === 0 && <p className="text-[10px] text-white/25 italic text-center mb-1.5">Auto order — none set</p>}
                <div className="grid gap-1.5" style={{ gridTemplateColumns: "repeat(5, minmax(0,1fr))" }}>
                    {Array.from({ length: SHOOT_PER_PAGE }, (_, i) => {
                        const num = (shootPage - 1) * SHOOT_PER_PAGE + i + 1;
                        const sh = shootRows[i];
                        return (
                            <div key={i} className="flex flex-col items-center" data-testid={`new-lineup-shooter-${num}`}>
                                <span className="font-heading text-lg font-black text-white/30 leading-none mb-1">{num}</span>
                                {sh ? <Pill slot={sh} color={c} playerMap={effMap} sz={TEXT_SIZES.small} op={op} />
                                    : <div className="w-full rounded-lg border border-dashed border-white/10" style={{ minHeight: 40 }} />}
                            </div>
                        );
                    })}
                </div>
                {shootPages > 1 && (
                    <div className="flex flex-wrap gap-1 mt-2 justify-center" data-testid="new-lineup-shootout-pager">
                        {Array.from({ length: shootPages }, (_, i) => <button key={i} onClick={() => setShootPage(i + 1)} data-testid={`new-lineup-shootout-page-${i + 1}`} className={`h-6 w-6 rounded text-[10px] font-bold ${shootPage === i + 1 ? "bg-[#9D4CDD] text-white" : "bg-white/10 text-white/55 hover:bg-white/20"}`}>{i + 1}</button>)}
                    </div>
                )}
            </>
        );
        return (
            <div className="mt-2 pt-2 border-t border-white/10" data-testid="new-lineup-shootout-inline">
                {labelStyle === "side"
                    ? <div className="flex items-stretch gap-1.5"><SectionLabel label={title} color={c} op={op} mode="side" /><div className="flex-1 min-w-0">{grid}</div></div>
                    : <><div className="mb-2"><SectionLabel label={title} color={c} op={op} mode="top" /></div>{grid}</>}
            </div>
        );
    };

    const renderBody = (w, color, sz) => {
        const key = w.key;
        const op = w.opacity ?? 100;
        const ed = editing && draft;
        const labelStyle = eff?.label_style || "top";
        const align = eff?.align || "center";
        const jItems = align === "left" ? "start" : align === "right" ? "end" : "center";
        const jContent = align === "left" ? "start" : align === "right" ? "end" : "center";
        // Uniform widget gap `G`, overridable PER SECTION via `section_gap[fld]`
        // (px). Lets forwards / special teams get their own tighter spacing.
        const G = Math.max(0, Number(w.spacing?.pad ?? 8));
        const secGap = (fld) => { const v = (w.section_gap || {})[fld]; return v == null || v === "" ? G : Math.max(0, Number(v)); };
        const secLabelGap = (fld) => secGap(fld);
        const secRowGap = (fld) => secGap(fld);
        const secEndGap = () => 0;
        const rowStyle = (fld) => ({ gap: secGap(fld), justifyContent: jContent });
        // Per-section text size + explicit card width/height (0 = auto).
        const szOf = (fld) => TEXT_SIZES[(w.section_text_size || {})[fld] || w.text_size || "auto"] || TEXT_SIZES.auto;
        const secBoxW = (fld) => { const v = Number((w.section_box_w || {})[fld] ?? 0); return v > 0 ? v : undefined; };
        const secBoxH = (fld) => { const v = Number((w.section_box_h || {})[fld] ?? 0); return v > 0 ? v : undefined; };
        // Section label — a full-width top header (default) OR a side team tab.
        const Labeled = ({ fld, label, color: lc, op: lo = 100, testid, children }) => (
            labelStyle === "side"
                ? <div className="flex items-stretch" style={{ gap: G }} data-testid={testid}><SectionLabel label={label} color={lc} op={lo} mode="side" /><div className="flex-1 min-w-0">{children}</div></div>
                : <div data-testid={testid}>
                    <div><SectionLabel label={label} color={lc} op={lo} mode="top" /></div>
                    <div style={{ marginTop: G }}>{children}</div>
                </div>
        );
        if (key === "lines") return fwdLines.map((l, i) => {
            const c = secColorOf(w, `fw${i}`, team);
            return (
                <Labeled key={i} fld={`fw${i}`} label={secTitleOf(w, `fw${i}`, LINE_LABELS[i])} color={c} op={op} testid={`new-lineup-row-${LINE_LABELS[i]}`}>
                    <div className="flex flex-nowrap items-start" style={rowStyle(`fw${i}`)}>
                        {Array.from({ length: 3 }, (_, j) => ed
                            ? <EditSlot key={j} dndId={`slot:fw-${i}-${j}`} val={l[j]} playerMap={byId} color={c} posDefault={["LW", "C", "RW"][j]} sz={szOf(`fw${i}`)} cardW={secBoxW(`fw${i}`)} cardH={secBoxH(`fw${i}`)} op={op} onShiftRemove={() => removeById(`slot:fw-${i}-${j}`)} onCaptain={() => cycleCaptain(`slot:fw-${i}-${j}`)} />
                            : <Pill key={j} slot={l[j]} color={c} posDefault={["LW", "C", "RW"][j]} playerMap={effMap} sz={szOf(`fw${i}`)} cardW={secBoxW(`fw${i}`)} cardH={secBoxH(`fw${i}`)} op={op} />)}
                    </div>
                </Labeled>
            );
        });
        if (key === "pairs") {
            return (
                <>
                    {dPairs.map((pr, i) => {
                        const c = secColorOf(w, `dp${i}`, team);
                        return (
                            <Labeled key={i} fld={`dp${i}`} label={secTitleOf(w, `dp${i}`, PAIR_LABELS[i])} color={c} op={op} testid={`new-lineup-row-${PAIR_LABELS[i]}`}>
                                <div className="flex flex-nowrap items-start" style={rowStyle(`dp${i}`)}>
                                    {Array.from({ length: 2 }, (_, j) => ed
                                        ? <EditSlot key={j} dndId={`slot:dp-${i}-${j}`} val={pr[j]} playerMap={byId} color={c} posDefault={["LD", "RD"][j]} sz={szOf(`dp${i}`)} cardW={secBoxW(`dp${i}`)} cardH={secBoxH(`dp${i}`)} op={op} onShiftRemove={() => removeById(`slot:dp-${i}-${j}`)} onCaptain={() => cycleCaptain(`slot:dp-${i}-${j}`)} />
                                        : <Pill key={j} slot={pr[j]} color={c} posDefault={["LD", "RD"][j]} playerMap={effMap} sz={szOf(`dp${i}`)} cardW={secBoxW(`dp${i}`)} cardH={secBoxH(`dp${i}`)} op={op} />)}
                                </div>
                            </Labeled>
                        );
                    })}
                </>
            );
        }
        if (key === "goalies") {
            // Own section — goalies stack VERTICALLY (like Reserves); Starter /
            // Backup render as matching section-title pills.
            const gc = secColorOf(w, "goalies", team);
            const n = Math.max(2, goalies.length);
            return (
                <div className="mx-auto w-fit" data-testid="new-lineup-goalies-list">
                    {Array.from({ length: n }, (_, j) => {
                        const lab = (G_LABELS[j] || `Goalie ${j + 1}`);
                        return (
                            <div key={j} data-testid={`new-lineup-goalie-${j}`} style={j === 0 ? undefined : { marginTop: secGap("goalies") }}>
                                <div style={{ marginBottom: secLabelGap("goalies") }}><SectionLabel label={lab} color={gc} op={op} mode="top" /></div>
                                {ed
                                    ? <EditSlot dndId={`slot:g-${j}`} val={goalies[j]} playerMap={byId} color={gc} tone="g" posDefault="G" sz={szOf("goalies")} cardW={secBoxW("goalies")} cardH={secBoxH("goalies")} op={op} onShiftRemove={() => removeById(`slot:g-${j}`)} onCaptain={() => cycleCaptain(`slot:g-${j}`)} />
                                    : <Pill slot={goalies[j]} color={gc} tone="g" posDefault="G" playerMap={effMap} sz={szOf("goalies")} cardW={secBoxW("goalies")} cardH={secBoxH("goalies")} op={op} />}
                            </div>
                        );
                    })}
                </div>
            );
        }
        if (key === "special") {
            const c1 = secColorOf(w, "u1", team), c2 = secColorOf(w, "u2", team);
            const unitEd = (uk, uc) => {
                const u = uk.includes("__2") ? "u2" : "u1"; const zf = szOf(u), bw = secBoxW(u), bh = secBoxH(u); const g = secGap(u);
                const rowOf = (start, count) => (
                    <div className="flex flex-nowrap items-start" style={{ gap: g, justifyContent: jContent }}>
                        {Array.from({ length: count }, (_, k) => { const i = start + k; return (
                            <EditSlot key={i} dndId={`su:${uk}:${i}`} val={(su[uk] || [])[i]} playerMap={byId} color={uc} posDefault={i < specFwd ? "F" : "D"} sz={zf} cardW={bw} cardH={bh} op={op} onShiftRemove={() => removeById(`su:${uk}:${i}`)} />
                        ); })}
                    </div>
                );
                return <div className="flex flex-col" style={{ gap: g }}>{rowOf(0, specFwd)}{rowOf(specFwd, Math.max(1, specN - specFwd))}</div>;
            };
            const unitRead = (uk, uc) => {
                const u = uk.includes("__2") ? "u2" : "u1"; const zf = szOf(u), bw = secBoxW(u), bh = secBoxH(u); const g = secGap(u);
                const slots = su5(uk, specN);
                const rowOf = (start, count) => (
                    <div className="flex flex-nowrap items-start" style={{ gap: g, justifyContent: jContent }}>
                        {Array.from({ length: count }, (_, k) => <Pill key={k} slot={slots[start + k]} color={uc} posDefault={(start + k) < specFwd ? "F" : "D"} playerMap={effMap} sz={zf} cardW={bw} cardH={bh} op={op} />)}
                    </div>
                );
                return <div className="flex flex-col" style={{ gap: g }}>{rowOf(0, specFwd)}{rowOf(specFwd, Math.max(1, specN - specFwd))}</div>;
            };
            const specToggles = (
                <div className="flex flex-wrap items-center gap-1.5" style={{ justifyContent: align === "left" ? "flex-start" : align === "right" ? "flex-end" : "center" }}>
                    <div className="inline-flex rounded-md border border-white/15 overflow-hidden bg-black/25" data-testid="new-lineup-spec-type">
                        {SPEC_TYPES.map(([k, l]) => <button key={k} onClick={() => setSpecType(k)} data-testid={`new-lineup-spec-type-${k}`} style={specType === k ? { backgroundColor: withA(resolveColor("primary", team), 22), color: "#fff" } : undefined} className={`px-2 py-1 text-[9px] font-black uppercase tracking-wider transition-colors text-white ${specType === k ? "" : "hover:bg-white/5"}`}>{l}</button>)}
                    </div>
                    <div className="inline-flex rounded-md border border-white/15 overflow-hidden bg-black/25" data-testid="new-lineup-spec-view">
                        {SPEC_VIEWS[specType].map((v) => <button key={v} onClick={() => setView(v)} data-testid={`new-lineup-spec-view-${v}`} style={view === v ? { backgroundColor: withA(resolveColor("primary", team), 22), color: "#fff" } : undefined} className={`px-2 py-1 text-[9px] font-black uppercase tracking-wider transition-colors text-white ${view === v ? "" : "hover:bg-white/5"}`}>{v}</button>)}
                    </div>
                </div>
            );
            return (
                <>
                    <Labeled fld="u1" label={secTitleOf(w, "u1", "Unit 1")} color={c1} op={op} testid="new-lineup-unit-Unit 1">
                        {ed ? unitEd(unitKey, c1) : unitRead(unitKey, c1)}
                    </Labeled>
                    <Labeled fld="u2" label={secTitleOf(w, "u2", "Unit 2")} color={c2} op={op} testid="new-lineup-unit-Unit 2">
                        {ed ? unitEd(`${unitKey}__2`, c2) : unitRead(`${unitKey}__2`, c2)}
                    </Labeled>
                    {specToggles}
                    {shootoutInline(op)}
                </>
            );
        }
        if (key === "shootout") return null;   // rendered inside the Special Teams box
        if (key === "reserves") {
            const order = Array.isArray(w.reserve_order) && w.reserve_order.length ? w.reserve_order : reserveSecs.map((r) => r[1]);
            const ordered = order.map((fld) => reserveSecs.find((r) => r[1] === fld)).filter(Boolean)
                .concat(reserveSecs.filter((r) => !order.includes(r[1])));
            const moveRes = (fld, dir) => {
                const cur = ordered.map((r) => r[1]);
                const i = cur.indexOf(fld); const j = i + dir;
                if (j < 0 || j >= cur.length) return;
                [cur[i], cur[j]] = [cur[j], cur[i]];
                patchWidget("reserves", { reserve_order: cur });
            };
            const blocks = ordered.map(([label, fld, arr], idx) => {
                const emptySec = !arr || arr.length === 0;
                if (emptySec && !ed) return null;   // empty sections hidden; in edit they stay mounted and only SHOW while dragging (CSS, no re-render)
                const secColor = secColorOf(w, fld, team);
                const secTitle = secTitleOf(w, fld, label);
                return (
                    <div key={fld} style={{ marginBottom: secRowGap(fld) }} className={emptySec ? "lineup-empty-sec" : undefined} data-testid={`new-lineup-reserve-${label}`}>
                        <div className="flex items-stretch gap-1" style={{ marginBottom: secLabelGap(fld) }}>
                            <div className="flex-1"><SectionLabel label={secTitle} color={secColor} op={op} mode="top" /></div>
                            {false && (
                                <div className="flex flex-col shrink-0">
                                    <button onClick={() => moveRes(fld, -1)} disabled={idx === 0} className="px-1 rounded hover:bg-white/10 disabled:opacity-25 text-white/60" title="Move earlier" data-testid={`new-lineup-reserve-up-${fld}`}><ChevronLeft className="w-3 h-3" /></button>
                                    <button onClick={() => moveRes(fld, 1)} disabled={idx === ordered.length - 1} className="px-1 rounded hover:bg-white/10 disabled:opacity-25 text-white/60" title="Move later" data-testid={`new-lineup-reserve-down-${fld}`}><ChevronRight className="w-3 h-3" /></button>
                                </div>
                            )}
                        </div>
                        {ed ? <EditList field={fld} items={arr} playerMap={byId} color={secColor} sz={szOf(fld)} cardW={secBoxW(fld)} cardH={secBoxH(fld)} gap={secGap(fld)} op={op} onShiftRemove={(i) => removeById(`${fld}:${i}`)} />
                            : <div>{arr.map((s, i) => <div key={i} style={i === 0 ? undefined : { marginTop: secGap(fld) }}><Pill slot={s} color={secColor} playerMap={effMap} sz={szOf(fld)} cardW={secBoxW(fld)} cardH={secBoxH(fld)} op={op} /></div>)}</div>}
                    </div>
                );
            });
            // 2 categories per column ONLY on mobile; desktop stacks single-column. Per-block spacing handles the gaps.
            return <div className={renderDevice === "mobile" ? "grid grid-cols-2 gap-x-2 mx-auto w-fit" : "w-fit"} data-testid="new-lineup-reserves-grid">{blocks}</div>;
        }
        return null;
    };

    const available = (() => {
        if (!editing || !draft) return [];
        const placed = placedSet(draft);
        return (editPool || []).filter((p) => !placed.has(p.id));
    })();

    // Quick Lines keyboard entry — ordered fixed slots (lines → pairs → goalies).
    const quickSlots = (() => {
        if (!draft) return [];
        const s = [];
        (draft.forward_lines || []).slice(0, 4).forEach((l, i) => ["LW", "C", "RW"].forEach((pos, j) => s.push({ key: `fw-${i}-${j}`, label: `Line ${i + 1} · ${pos}`, pos })));
        (draft.d_pairs || []).slice(0, 3).forEach((l, i) => ["LD", "RD"].forEach((pos, j) => s.push({ key: `dp-${i}-${j}`, label: `Pair ${i + 1} · ${pos}`, pos })));
        (draft.goalies || []).slice(0, 2).forEach((g, j) => s.push({ key: `g-${j}`, label: j === 0 ? "Starter (G)" : "Backup (G)", pos: "G" }));
        return s;
    })();
    const quickValueOf = (key) => (draft ? getEV(draft, key) : null);
    const quickPick = (key, p) => { const d = clone(draft); removeExclusive(d, String(p.id)); writeEV(d, key, { player_id: String(p.id), player_name: p.name || "" }); setDraft(d); };
    const quickClear = (key) => { const d = clone(draft); writeEV(d, key, { ...EMPTY }); setDraft(d); };
    const quickBatch = (placements) => {
        setDraft((prev) => {
            const d = clone(prev || {});
            d.reserves = d.reserves || [];
            (placements || []).forEach(({ key, id, name }) => {
                removeExclusive(d, String(id));
                if (key === "__reserve__") d.reserves = [...d.reserves, { label: "Scratch", position: "", player_id: String(id), player_name: name || "" }];
                else writeEV(d, key, { player_id: String(id), player_name: name || "" });
            });
            return d;
        });
    };

    // ---- Mobile tap-to-assign: tap a pool player → pick a slot (best-fit first) ----
    const slotGroupOf = (pos) => pos === "G" ? "G" : (pos === "LD" || pos === "RD") ? "D" : "F";
    const tapSlots = (() => {
        if (!tapPlayer || !draft) return [];
        const pg = posTag(tapPlayer);
        const pp = (tapPlayer.position || "").toUpperCase();
        const rank = (slot) => { const sg = slotGroupOf(slot.pos); if (pg !== sg) return 2; if (pp === slot.pos) return 0; return 1; };
        return quickSlots
            .map((s) => ({ ...s, occ: getEV(draft, s.key), r: rank(s) }))
            .sort((a, b) => a.r - b.r || ((a.occ?.player_id ? 1 : 0) - (b.occ?.player_id ? 1 : 0)));
    })();
    const tapAssign = (key) => { if (tapPlayer) quickPick(key, tapPlayer); setTapPlayer(null); };
    const tapScratch = () => {
        if (!tapPlayer || !draft) return;
        const d = clone(draft);
        removeExclusive(d, String(tapPlayer.id));
        d.reserves = [...(d.reserves || []), { label: "Scratch", position: "", player_id: String(tapPlayer.id), player_name: tapPlayer.name || "" }];
        setDraft(d);
        setTapPlayer(null);
    };

    const badgeCtxVal = { accent: teamAccent, uiAccent, nhlTheme, isNhl: nhlTheme || adapter !== PWHL_LINEUP_ADAPTER, badges: badgePos, nudges: badgeNudges, badgeStyle: eff?.badge_style || "team", boxStyle: eff?.box_style || "dark", boxShape: eff?.box_shape || "rounded", boxSize: eff?.box_size || "md", boxOpacity: Number(eff?.box_opacity ?? 85), jerseySize: eff?.jersey_size || "md", positionSize: eff?.position_size || "md", jerseyHash: eff?.jersey_hash === true || eff?.jersey_hash === "true", liveIdentity: (!gameCentre && target === "active") };
    const board = (
        <BadgeCtx.Provider value={badgeCtxVal}>
        <div className={`flex gap-3 ${renderDevice === "mobile" ? "flex-col items-stretch" : `flex-wrap items-start ${(gameCentre || hoistToNav) ? "justify-center" : "justify-start"}`}`} data-testid="new-lineup-board">
            {widgets.filter((w) => w.key !== "shootout" && !(gameCentre && w.key === "reserves")).map((w, idx) => {
                const color = resolveColor(w.color, team, w.custom);
                const sz = TEXT_SIZES[w.text_size || "auto"] || TEXT_SIZES.auto;
                // Goalies & Reserves hold narrow content → size to content and centre
                // (never full-width) so their title pills never exceed the player boxes.
                const narrow = w.key === "goalies" || w.key === "reserves";
                const mob = renderDevice === "mobile";
                return (
                    <section key={w.key} className={nhlTheme ? "rounded-md bg-white/[0.02] border border-white/10 flex flex-col overflow-hidden" : "rounded-2xl bg-[#0B0C13] border border-white/10 flex flex-col"} style={{ flex: (mob && !narrow) ? "1 1 100%" : "0 0 auto", width: mob ? "max-content" : undefined, minWidth: mob ? "100%" : 0, maxWidth: mob ? "none" : "100%", alignSelf: (mob && narrow) ? "center" : undefined, padding: nhlTheme ? 0 : Math.max(0, Number(w.spacing?.pad ?? 8)), gap: nhlTheme ? 0 : Math.max(0, Number(w.spacing?.pad ?? 8)) }} data-testid={`new-lineup-${w.key === "lines" ? "forwards" : w.key === "pairs" ? "pairs" : w.key === "special" ? "special-teams" : w.key}`}>
                        <WidgetHeader w={w} admin={false} isFirst={idx === 0} isLast={idx === widgets.length - 1} team={team} color={color} op={w.opacity ?? 100} fill={narrow}
                            onMove={(dir) => moveWidget(w.key, dir)} editing={editKey === w.key}
                            onEdit={() => setEditKey(editKey === w.key ? null : w.key)} onClose={() => setEditKey(null)}
                            onChange={(patch) => patchWidget(w.key, patch)} onSave={() => { saveCfg.mutate(cfg); setEditKey(null); }} />
                        <div style={nhlTheme ? { padding: Math.max(8, Number(w.spacing?.pad ?? 8)), display: "flex", flexDirection: "column", gap: Math.max(8, Number(w.spacing?.pad ?? 8)) } : undefined}>
                            {renderBody(w, color, sz)}
                        </div>
                    </section>
                );
            })}
        </div>
        </BadgeCtx.Provider>
    );

    // Choose-lineup + save controls. When hoisted (team Lineups tab) the picker +
    // overview live in the site nav (nav bar 3); only the admin edit buttons stay
    // in the body. Otherwise the full strip renders inline (e.g. game centre).
    const adminEditButtons = (editing && draft) ? (
        <>
            <button type="button" onClick={onSave} disabled={saving} data-testid="new-lineup-save-btn"
                style={{ background: uiAccent }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider text-white hover:brightness-110 disabled:opacity-50">
                <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save"}
            </button>
            <button type="button" onClick={() => setQuickLines((v) => !v)} data-testid="new-lineup-quicklines-toggle"
                style={quickLines ? { background: uiAccent, borderColor: uiAccent } : { background: "rgba(255,255,255,0.1)", borderColor: "rgba(255,255,255,0.3)" }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider text-white border hover:brightness-110">
                <Keyboard className="h-3.5 w-3.5" /> Quick Lines
            </button>
            <button type="button" onClick={undo} disabled={!canUndo} data-testid="new-lineup-undo-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider bg-white/10 text-white border border-white/30 hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed">
                <Undo2 className="h-3.5 w-3.5" /> Undo
            </button>
            <button type="button" onClick={redo} disabled={!canRedo} data-testid="new-lineup-redo-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider bg-white/10 text-white border border-white/30 hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed">
                <Redo2 className="h-3.5 w-3.5" /> Redo
            </button>
            {gameCentre && selGame && (
                <button type="button" onClick={loadOpening} disabled={openingBusy} data-testid="new-lineup-opening-btn"
                    title="Load the 6 players who started this game (opening lineup)"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider bg-[#F6C453]/15 text-[#F6C453] border border-[#F6C453]/40 hover:bg-[#F6C453]/25 disabled:opacity-50">
                    <Flag className="h-3.5 w-3.5" /> {openingBusy ? "Loading…" : "Use Opening Lineup"}
                </button>
            )}
            {note && <span className="text-xs text-[#86EFAC]" data-testid="new-lineup-note">{note}</span>}
        </>
    ) : null;

    const overviewLink = (
        <Link to={`/teams/${teamId}?tab=overview`} data-testid="new-lineup-to-overview" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white/45 hover:text-white transition-colors">Overview</Link>
    );

    const editBar = adminEditButtons ? <div className="flex flex-wrap items-center gap-2" data-testid="new-lineup-editbar">{adminEditButtons}</div> : null;
    const strip = hoistToNav ? null : (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[#1E2233] bg-[#0E0F16] px-4 py-3" data-testid="new-lineup-strip">
            {team?.logo_url && <img src={assetUrl(team.logo_url)} alt={team.name} className="h-9 w-9 object-contain shrink-0" />}
            <div className="min-w-0">
                <h1 className="font-heading text-xl font-black uppercase tracking-tight text-white leading-none truncate">{team?.name}</h1>
                <p className="text-[10px] uppercase tracking-[0.16em] text-[#94A3B8] mt-0.5">{adapter.renderPicker ? "Game Lineup" : "Active Lineup"}</p>
            </div>
            <span className="ml-auto">{adapter.renderPicker ? null : overviewLink}</span>
        </div>
    );

    const setBadgeStyle = (v) => dset("badge_style", v);
    const setOpt = (key, v) => dset(key, v);
    const optSelect = (key, label, opts, dflt) => (
        <label className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-white/55">{label}
            <select value={dget(key, dflt)} onChange={(e) => setOpt(key, e.target.value)} data-testid={`new-lineup-opt-${key}`}
                className="bg-[#0A0B10] border border-[#1E2233] rounded-md px-2 py-1 text-[11px] text-white focus:outline-none focus:border-[#9D4CDD]">
                {opts.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
            </select>
        </label>
    );
    const widgetAdminBar = (designEdit && editing && draft) ? (
        <BadgeCtx.Provider value={badgeCtxVal}>
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-[#1E2233] bg-[#0E0F16] px-4 py-2.5" data-testid="new-lineup-widget-admin">
                <span className="text-[10px] font-black uppercase tracking-[0.16em] text-[#B366F0] mr-1">Widgets</span>
                {widgets.filter((w) => w.key !== "shootout").map((w, idx, arr) => (
                    <div key={w.key} className="w-[190px]">
                        <WidgetHeader w={w} admin isFirst={idx === 0} isLast={idx === arr.length - 1} team={team} color={resolveColor(w.color, team, w.custom)} op={w.opacity ?? 100} fill
                            onMove={(dir) => moveWidget(w.key, dir)} editing={editKey === w.key}
                            onEdit={() => setEditKey(editKey === w.key ? null : w.key)} onClose={() => setEditKey(null)}
                            onChange={(patch) => patchWidget(w.key, patch)} onSave={() => { saveCfg.mutate(cfg); setEditKey(null); }} />
                    </div>
                ))}
            </div>
        </BadgeCtx.Provider>
    ) : null;
    const badgeSettingsBar = (designEdit && editing && draft) ? (
        <div className="flex flex-col gap-2 rounded-xl border border-[#1E2233] bg-[#0E0F16] px-4 py-2.5" data-testid="new-lineup-badge-settings">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <span className="text-[10px] font-black uppercase tracking-[0.16em] text-[#B366F0]">Editing</span>
                <div className="inline-flex rounded-md border border-[#1E2233] overflow-hidden" data-testid="new-lineup-device-toggle">
                    {[["desktop", "Desktop"], ["mobile", "Mobile"]].map(([d, l]) => (
                        <button key={d} onClick={() => setDevice(d)} data-testid={`new-lineup-device-${d}`}
                            style={device === d ? { background: uiAccent } : undefined}
                            className={`px-3 py-1 text-[10px] font-black uppercase tracking-wider transition-colors ${device === d ? "text-white" : "text-white/45 hover:bg-white/5"}`}>{l}</button>
                    ))}
                </div>
                <span className="text-[10px] text-white/35 italic">{device === "mobile" ? "Editing the mobile layout — auto-applied on phones" : "Editing the desktop layout"}</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <span className="text-[10px] font-black uppercase tracking-[0.16em] text-[#B366F0]">Player Box</span>
                {optSelect("box_style", "Fill", [["dark", "Dark"], ["black", "Black"], ["team", "Team colour"], ["none", "None"]], "dark")}
                {optSelect("box_opacity", "Opacity", [["100", "100%"], ["85", "85%"], ["70", "70%"], ["55", "55%"], ["40", "40%"], ["25", "25%"]], "85")}
                {optSelect("box_shape", "Shape", [["rounded", "Rounded"], ["square", "Square"], ["circle", "Circle"]], "rounded")}
                {optSelect("box_size", "Size", [["sm", "Compact"], ["md", "Standard"], ["lg", "Roomy"]], "md")}
                {optSelect("label_style", "Section labels", [["top", "Top headers"], ["side", "Side tabs"]], "top")}
                {optSelect("align", "Align", [["center", "Centre"], ["left", "Left"], ["right", "Right"]], "center")}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <span className="text-[10px] font-black uppercase tracking-[0.16em] text-[#B366F0]">Badges</span>
                <label className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-white/55">Style
                    <select value={dget("badge_style", "team")} onChange={(e) => setBadgeStyle(e.target.value)} data-testid="new-lineup-badge-style"
                        className="bg-[#0A0B10] border border-[#1E2233] rounded-md px-2 py-1 text-[11px] text-white focus:outline-none focus:border-[#9D4CDD]">
                        <option value="team">Team colour</option>
                        <option value="bw">Black & white</option>
                        <option value="position">Position colour</option>
                    </select>
                </label>
                {[["jersey", "Jersey #"], ["captain", "C / A"], ["position", "Position"]].map(([kind, lbl]) => (
                    <label key={kind} className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-white/55">{lbl}
                        <select value={cornerOf(kind)} onChange={(e) => setBadge(kind, e.target.value)} data-testid={`new-lineup-badge-pos-${kind}`}
                            className="bg-[#0A0B10] border border-[#1E2233] rounded-md px-2 py-1 text-[11px] text-white focus:outline-none focus:border-[#9D4CDD]">
                            {BADGE_PLACEMENTS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                        </select>
                    </label>
                ))}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <span className="text-[10px] font-black uppercase tracking-[0.16em] text-[#B366F0]">Badge Sizes & Nudge</span>
                {optSelect("jersey_size", "Jersey #", [["sm", "Small"], ["md", "Medium"], ["lg", "Large"]], "md")}
                {optSelect("position_size", "Position", [["sm", "Small"], ["md", "Medium"], ["lg", "Large"]], "md")}
                {optSelect("jersey_hash", "Jersey prefix", [["false", "No #"], ["true", "Show #"]], "false")}
                {optSelect("jersey_nudge", "Jersey ↔", [["0", "«3"], ["1", "«2"], ["2", "«1"], ["3", "•"], ["4", "1»"], ["5", "2»"], ["6", "3»"]], "3")}
                {optSelect("position_nudge", "Position ↔", [["0", "«3"], ["1", "«2"], ["2", "«1"], ["3", "•"], ["4", "1»"], ["5", "2»"], ["6", "3»"]], "3")}
                {optSelect("captain_nudge", "C/A ↔", [["0", "«3"], ["1", "«2"], ["2", "«1"], ["3", "•"], ["4", "1»"], ["5", "2»"], ["6", "3»"]], "3")}
            </div>
        </div>
    ) : null;

    return (
        <div className="space-y-3">
            {strip}
            {!hoistToNav && adapter.renderPicker && adapter.renderPicker({ teamId, target, onTarget: setTarget, fixtures, team })}
            {selGame && adapter.gameHref(selGame) && (
                <div className="flex justify-end">
                    <Link to={adapter.gameHref(selGame)} data-testid="new-lineup-to-game" className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#C99BF0] hover:text-white transition-colors">
                        View this game <ChevronRight className="h-3.5 w-3.5" />
                    </Link>
                </div>
            )}
            {editing && draft ? (
                <DragDropContext onBeforeCapture={() => document.body.classList.add("lineup-dnd")} onDragStart={pointerDragStart} onDragEnd={(r) => { document.body.classList.remove("lineup-dnd"); onDragEnd(pointerDragEnd(r)); }}>
                    {board}
                    <BadgeCtx.Provider value={badgeCtxVal}>
                    <div className="rounded-2xl bg-[#0B0C13] border border-white/10 p-4" data-testid="new-lineup-pool">
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="font-heading text-sm font-bold uppercase tracking-wider text-[#E9D5FF]">Available Players <span className="text-white/40 ml-1">({available.length})</span></h4>
                            <span className="text-[10px] text-white/40 uppercase tracking-wider">{vwMobile ? <>Tap a player to assign · or <span className="text-[#C9A6F0] font-bold">Shift-click</span></> : <>Drag · or <span className="text-[#C9A6F0] font-bold">Shift-click</span> to auto-place</>}</span>
                        </div>
                        <Droppable droppableId="pool" direction="horizontal" type="PLAYER">
                            {(prov, snap) => (
                                <div ref={prov.innerRef} {...prov.droppableProps} style={snap.isDraggingOver ? { background: `${uiAccent}1a`, boxShadow: `inset 0 0 0 1px ${uiAccent}` } : undefined} className={`min-h-[80px] rounded-md p-2 flex flex-wrap gap-3 content-start transition-colors ${snap.isDraggingOver ? "" : "bg-black/20"}`}>
                                    {available.length === 0 && !snap.isDraggingOver ? <p className="text-xs text-white/40 italic py-4 w-full text-center">All players placed — drag one back here or shift-click a placed player.</p>
                                        : available.map((p, i) => (
                                            <Draggable key={p.id} draggableId={`pool:${p.id}`} index={i}>
                                                {(pr, snap) => withPortal(<div ref={pr.innerRef} {...pr.draggableProps} style={zoomDragStyle(pr.draggableProps.style)}><PoolChip dndProps={pr.dragHandleProps} p={p} name={p.name} tag={p.position || posTag(p)} sz={TEXT_SIZES.auto} onShiftAdd={() => autoAdd(p.id)} onCtrlAdd={() => sendToSpecialUnit(p.id)} onTap={vwMobile ? () => setTapPlayer(p) : undefined} /></div>, snap.isDragging)}
                                            </Draggable>
                                        ))}
                                    <div style={{ display: "none" }}>{prov.placeholder}</div>
                                </div>
                            )}
                        </Droppable>
                    </div>
                    </BadgeCtx.Provider>
                </DragDropContext>
            ) : board}
            {/* ADMIN tools live BELOW the lineup so toggling admin never moves the public view. */}
            {editBar}
            {editing && draft && quickLines && (
                <QuickLines slots={quickSlots} players={editPool} valueOf={quickValueOf} onPick={quickPick} onClear={quickClear} onBatch={quickBatch} accent={uiAccent} />
            )}
            {widgetAdminBar}
            {badgeSettingsBar}
            {selGame && editing && <p className="text-[11px] text-white/45 px-1">Pool = the players who dressed for this game (incl. injured / since-departed). Drag them into the lineup, then Save — it shows on the game centre <span className="text-[#86EFAC] font-bold">automatically</span> as soon as one player is placed. Use the <span className="text-[#C9A6F0] font-bold">C/A</span> chip on a placed player to set captain / alternate.</p>}
            {!editing && (
                <p className="text-[11px] text-white/40 italic leading-relaxed px-1 max-w-3xl" data-testid="lineup-disclaimer">
                    Lineups can change a lot during a game, so what's shown here — often just the starting lineup — is a little inaccurate by the final buzzer. Rosterly doesn't track lineup stats because we find them mostly irrelevant; that's where the <Link to={nhlTheme || adapter !== PWHL_LINEUP_ADAPTER ? "/nhl/chemistry" : "/chemistry"} className="text-[#C9A6F0] font-bold not-italic hover:underline">Chemistry</Link> page comes in.
                </p>
            )}
            {editing && draft && tapPlayer && (
                <div className="fixed inset-0 z-[80] flex items-end justify-center lg:items-center" data-testid="tap-assign-sheet">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setTapPlayer(null)} data-testid="tap-assign-overlay" />
                    <div className="relative w-full max-w-md max-h-[75vh] overflow-y-auto rounded-t-2xl lg:rounded-2xl border-t border-x lg:border border-white/15 bg-[#0B0C13] p-4 pb-[calc(16px+env(safe-area-inset-bottom))] animate-slide-up shadow-2xl shadow-black/60">
                        <div className="flex items-start justify-between mb-3">
                            <div className="min-w-0">
                                <p className="text-[10px] uppercase tracking-[0.16em] text-white/40">Assign player</p>
                                <h4 className="font-heading text-base font-black uppercase tracking-tight text-white leading-tight truncate">
                                    {tapPlayer.name}
                                    {tapPlayer.position ? <span className="ml-2 text-[11px] text-[#C9A6F0] align-middle">{tapPlayer.position}</span> : null}
                                </h4>
                            </div>
                            <button type="button" onClick={() => setTapPlayer(null)} data-testid="tap-assign-close"
                                className="shrink-0 h-8 w-8 rounded-lg border border-white/15 text-white/60 flex items-center justify-center active:bg-white/10">
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            {tapSlots.map((s) => {
                                const occupied = !!s.occ?.player_id;
                                const best = s.r === 0;
                                return (
                                    <button key={s.key} type="button" onClick={() => tapAssign(s.key)} data-testid={`tap-assign-slot-${s.key}`}
                                        style={best ? { borderColor: uiAccent, background: `${uiAccent}1f` } : undefined}
                                        className="text-left rounded-lg border border-white/12 bg-white/[0.03] px-3 py-2 active:brightness-125 transition-colors">
                                        <div className="flex items-center justify-between gap-1">
                                            <span className="text-[11px] font-black uppercase tracking-wider text-white leading-none truncate">{s.label}</span>
                                            {best && <span className="shrink-0 text-[8px] font-black uppercase tracking-wider px-1 py-0.5 rounded" style={{ background: uiAccent, color: "#0A0B10" }}>Best</span>}
                                        </div>
                                        <span className="block mt-1 text-[10px] text-white/50 truncate">{occupied ? `Swap · ${s.occ.player_name}` : "Open slot"}</span>
                                    </button>
                                );
                            })}
                        </div>
                        <button type="button" onClick={tapScratch} data-testid="tap-assign-scratch"
                            className="mt-3 w-full rounded-lg border border-white/12 bg-white/[0.03] px-3 py-2 text-[11px] font-black uppercase tracking-wider text-white/70 active:bg-white/10">
                            Healthy Scratch
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// Reusable editing engine + Quick Lines panel — so the COMPACT view can offer
// the EXACT same drag-and-drop / shift-click / captain / placement behaviour.
export { EMPTY, clone, getEV, writeEV, removeExclusive, placedSet, posTag, parseId, clearSource, hydrate, QuickLines };

export default NewLineupBoard;
